
export const translations = {
  en: {
    ".": ".",
    appName: "OneClickSell",
    trustAppName: "OneClickSell Trust",

    // Navigation
    nav: {
      features: "Features",
      pricing: "Pricing",
      faq: "FAQ",
      login: "Log In",
      startFree: "Start Free"
    },

    appNav: {
      more: "More",
      loginHint: "Log in to access this feature.",
      unlockHint: "Complete an import to unlock."
    },

    admin: {
      title: "Admin Console",
      subtitle: "Operations & Controls",
      backToWorkspace: "Back to Workspace",
      login: {
        title: "Admin Login",
        subtitle: "Use your admin credentials to continue.",
        checking: "Checking admin session...",
        username: "Username",
        usernamePlaceholder: "Enter admin username",
        password: "Password",
        passwordPlaceholder: "Enter admin password",
        action: "Log In",
        loggingIn: "Logging in...",
        required: "Username and password are required.",
        failed: "Login failed.",
        denied: "Access denied.",
        logout: "Log Out"
      },
      nav: {
        overview: "Overview",
        aiChannels: "AI Channels",
        aiMonitor: "Channel Monitor",
        billing: "Billing & Credits",
        consumption: "Consumption",
        users: "Users",
        logs: "Admin Logs",
        settings: "System Settings"
      },
      access: {
        checking: "Verifying admin access...",
        loginTitle: "Please log in to continue",
        loginDesc: "Admin console requires authentication.",
        loginAction: "Log In",
        deniedTitle: "Access denied",
        deniedDesc: "Your account does not have admin permissions."
      },
      overview: {
        title: "Dashboard",
        desc: "Key metrics and recent trends",
        loading: "Loading overview data...",
        error: "Failed to load overview data.",
        empty: "No data yet.",
        userTrend: "New users in last 7 days",
        revenueTrend: "Revenue in last 7 days",
        cards: {
          totalUsers: "Total Users",
          activeToday: "Active Today",
          newToday: "New Today",
          totalRevenue: "Total Revenue",
          revenueMonth: "Revenue This Month",
          totalConsumption: "Total Consumption"
        }
      },
      aiChannels: {
        title: "AI Channel Management",
        desc: "Manage platform AI providers and routing",
        error: "Failed to load channels.",
        validationName: "Channel name is required.",
        validationKey: "API Key is required.",
        updated: "Channel updated.",
        created: "Channel created.",
        tested: "Test completed.",
        deleteConfirm: "Delete channel {name}?",
        refresh: "Refresh",
        updatedAt: "Updated at {time}",
        edit: "Edit Channel",
        create: "Create Channel",
        cancel: "Cancel",
        formHint: "Keep API Key empty when editing to preserve current key.",
        summary: {
          total: "Total Channels",
          enabled: "Enabled",
          degraded: "Degraded",
          requests: "24h Requests",
          failureRate: "24h Failure Rate"
        },
        fields: {
          name: "Channel Name",
          apiKey: "API Key",
          baseUrl: "Base URL",
          tier: "Tier",
          priority: "Priority",
          maxFail: "Max Fail Count",
          recovery: "Recovery Time (s)",
          description: "Description",
          enabled: "Enabled",
          isOfficial: "Official"
        },
        placeholders: {
          name: "e.g. Gemini-Primary",
          apiKey: "Paste API key",
          apiKeyEdit: "Leave blank to keep current key",
          baseUrl: "https://api.example.com",
          description: "Usage notes"
        },
        save: "Save",
        saving: "Saving...",
        listTitle: "Channel List",
        loading: "Loading channels...",
        empty: "No channels configured.",
        statusUnknown: "Unknown",
        table: {
          name: "Channel",
          status: "Status",
          priority: "Priority",
          baseUrl: "Base URL",
          requests: "24h Requests",
          failures: "24h Failures",
          successRate: "Success Rate",
          avgLatency: "Avg Latency",
          actions: "Actions"
        },
        actions: {
          edit: "Edit",
          disable: "Disable",
          enable: "Enable",
          test: "Test",
          delete: "Delete"
        }
      },
      aiMonitor: {
        title: "AI Channel Monitor",
        desc: "Runtime status and routing health",
        loading: "Loading runtime stats...",
        error: "Failed to load channel stats.",
        refresh: "Refresh",
        autoRefresh: "Auto Refresh",
        updatedAt: "Updated at {time}",
        current: "Current Channel",
        total: "Total Channels",
        healthy: "Healthy Channels",
        requests: "24h Requests",
        failureRate: "Failure Rate",
        avgLatency: "Avg Latency",
        trend: "Traffic Trend",
        last24h: "Last 24h",
        noTrend: "No trend data",
        errors: "Recent Errors",
        errorsDesc: "Latest failures in last 24h",
        noErrors: "No recent errors",
        none: "None",
        empty: "No runtime data.",
        statusUnknown: "Unknown",
        yes: "Yes",
        no: "No",
        table: {
          name: "Channel",
          status: "Status",
          priority: "Priority",
          enabled: "Enabled",
          available: "Available",
          successRate: "Success Rate",
          failCount: "Fail Count",
          avgLatency: "Avg Latency"
        }
      },
      billing: {
        title: "Credits & Billing",
        desc: "Adjust credits and run manual recharges",
        error: "Failed to load credit users.",
        actionTitle: "Manual credit action",
        validationUser: "User ID is required.",
        defaultReason: "Admin adjustment",
        rechargeReason: "Admin recharge",
        actionSuccess: "Action completed.",
        submitting: "Submitting...",
        submit: "Submit",
        listTitle: "User Credits",
        search: "Search",
        loading: "Loading credit users...",
        empty: "No users found.",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        fields: {
          userId: "User ID",
          actionType: "Action",
          reason: "Reason",
          credits: "Credits",
          amount: "Amount",
          bonus: "Bonus"
        },
        placeholders: {
          userId: "Select from list or paste ID",
          reason: "Optional reason",
          search: "Search user"
        },
        actions: {
          adjust: "Adjust Credits",
          recharge: "Recharge",
          select: "Select"
        },
        table: {
          user: "User",
          plan: "Plan",
          credits: "Credits",
          trial: "Trial",
          actions: "Actions"
        }
      },
      consumption: {
        title: "Consumption Records",
        desc: "Track credit usage by user and task",
        loading: "Loading consumption records...",
        error: "Failed to load consumption records.",
        empty: "No records found.",
        search: "Search",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          userId: "Filter by user ID"
        },
        table: {
          user: "User",
          product: "Product",
          source: "Source",
          unitPrice: "Unit Price",
          createdAt: "Created At"
        }
      },
      users: {
        title: "User Management",
        desc: "Update status, plans, and admin flags",
        error: "Failed to load users.",
        filters: "Filters",
        search: "Search",
        filtersAllStatus: "All status",
        filtersAllAdmin: "All users",
        filtersAdmin: "Admins only",
        filtersNonAdmin: "Non-admins",
        editorTitle: "Edit user",
        detailTitle: "User details",
        closeEditor: "Close",
        adminToggle: "Admin access",
        saved: "Changes saved.",
        saving: "Saving...",
        save: "Save changes",
        listTitle: "Users",
        loading: "Loading users...",
        empty: "No users found.",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        fields: {
          status: "Status",
          plan: "Plan",
          admin: "Admin",
          notes: "Notes"
        },
        detailLabels: {
          userId: "User ID",
          phone: "Phone",
          email: "Email",
          plan: "Plan",
          status: "Status",
          credits: "Credits",
          trial: "Trial",
          createdAt: "Created At",
          lastLogin: "Last Login"
        },
        placeholders: {
          search: "Search by name / phone / email",
          notes: "Admin notes"
        },
        status: {
          active: "Active",
          inactive: "Inactive",
          banned: "Banned"
        },
        plan: {
          free: "Free",
          earlyBird: "Early Bird",
          standard: "Standard"
        },
        table: {
          user: "User",
          plan: "Plan",
          status: "Status",
          credits: "Credits",
          actions: "Actions"
        },
        actions: {
          manage: "Manage"
        }
      },
      logs: {
        title: "Admin Logs",
        desc: "Audit trail of admin operations",
        loading: "Loading logs...",
        error: "Failed to load logs.",
        empty: "No logs found.",
        search: "Search",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          action: "Filter by action",
          admin: "Filter by admin ID"
        },
        table: {
          action: "Action",
          target: "Target",
          admin: "Admin",
          details: "Details",
          ip: "IP",
          createdAt: "Created At"
        }
      },
      settings: {
        title: "System Settings",
        desc: "Adjust runtime configuration",
        error: "Failed to load settings.",
        loading: "Loading settings...",
        empty: "No settings available.",
        saved: "Setting saved.",
        save: "Save",
        boolean: "Enabled",
        updatedAt: "Updated at {time}",
        groups: {
          business: "Business",
          feature: "Feature",
          other: "Other"
        }
      }
    },

    // Hero Section
    hero: {
      badge: "AI-Powered E-commerce",
      title: "Transform Your Products",
      titleHighlight: "Into Global Listings",
      subtitle: "Paste a link, let AI optimize everything, publish to marketplaces in one click.",
      placeholder: "Paste your 1688 or AliExpress link here...",
      button: "Start AI Optimization"
    },

    // Feature 1 - Product Showcase
    feature1: {
      title: "See AI Magic in Action",
      subtitle: "Watch how we transform raw supplier data into premium marketplace listings.",
      tab1: "Smart Watch",
      tab2: "Headphones",
      tab3: "Sneakers",
      imgBtn1: "AI Enhanced",
      imgBtn2: "Original",
      imgBtn3: "Compare",
      aiBadge: "AI Optimized",
      marketingTitle: "Marketing Assets",
      viewAll: "View All",
      listingTitleTag: "Listing Title",
      translated: "Translated",
      bulletTitle: "Key Features",
      seoTitle: "SEO Keywords",
      pushBtn: "Push to Marketplace",
      syncText: "Synced to Ozon",
      // Watch product
      prodTitle_watch: "Premium Smart Watch Pro Max",
      bullet1Title_watch: "Health Monitoring",
      bullet1Desc_watch: "24/7 heart rate, SpO2, sleep tracking",
      bullet2Title_watch: "Long Battery",
      bullet2Desc_watch: "Up to 14 days on single charge",
      key1_watch: "smartwatch",
      key2_watch: "fitness tracker",
      key3_watch: "health monitor",
      hotspot1: "AMOLED Display",
      hotspot2: "Titanium Case",
      // Headphones product
      prodTitle_headphones: "Wireless ANC Headphones Studio",
      bullet1Title_headphones: "Active Noise Cancellation",
      bullet1Desc_headphones: "Block out 99% of ambient noise",
      bullet2Title_headphones: "Hi-Res Audio",
      bullet2Desc_headphones: "40mm drivers with LDAC support",
      key1_headphones: "wireless headphones",
      key2_headphones: "noise cancelling",
      key3_headphones: "bluetooth",
      hotspot_headphones_1: "Memory Foam Pads",
      hotspot_headphones_2: "Touch Controls",
      // Sneakers product
      prodTitle_sneakers: "Ultra Boost Running Shoes X9",
      bullet1Title_sneakers: "Cloud Cushioning",
      bullet1Desc_sneakers: "Revolutionary foam technology",
      bullet2Title_sneakers: "Breathable Mesh",
      bullet2Desc_sneakers: "360° ventilation system",
      key1_sneakers: "running shoes",
      key2_sneakers: "athletic footwear",
      key3_sneakers: "sports sneakers",
      hotspot_sneakers_1: "Carbon Plate",
      hotspot_sneakers_2: "Grip Sole"
    },

    // Feature 2 - How It Works
    feature2: {
      title: "How It Works",
      subtitle: "Three simple steps to global marketplace success.",
      card1Title: "Import & Analyze",
      card1Desc: "Paste any supplier link. Our AI extracts product data, images, and specifications automatically.",
      card2Title: "AI Optimization",
      card2Desc: "Generate SEO titles, translate descriptions, enhance images, and create A+ content in seconds."
    },

    // Feature 3 - Security
    feature3: {
      badge: "Enterprise Security",
      title: "Protect Your Brand Assets",
      subtitle: "Military-grade protection for your product images and listings.",
      verified: "Verified",
      watermark: "Neural Watermark",
      hash: "SHA-256 Hash",
      cert: "Certified",
      card1Title: "Invisible Watermarks",
      card1Desc: "AI-embedded markers that survive compression and cropping.",
      card2Title: "Legal Evidence Pack",
      card2Desc: "Auto-generated DMCA takedown documents with blockchain proof.",
      card3Title: "Instant Detection",
      card3Desc: "Get alerts when your images appear on unauthorized listings.",
      downloadBtn: "Download Whitepaper",
      downloadSub: "Security Architecture Overview",
      learnMore: "Learn More About Protection"
    },

    // Pricing Section
    pricing: {
      title: "Simple, Transparent Pricing",
      subtitle: "Only pay for image generation — 2 credits per image.",
      monthly: "Monthly",
      yearly: "Yearly",
      save: "2 months free",
      bestForGuests: "Best for Guests",
      onDemand: "Pay as you go",
      credits: "Credits",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      perMonth: "/mo",
      perYear: "/yr",
      packBasic: "Basic Pack",
      packGrowth: "Growth Pack",
      packScale: "Scale Pack",
      bestValue: "Best Value",
      featExpires: "Top-up credits never expire",
      featAi: "AI image generation",
      featPsd: "HD image exports",
      featPriority: "Priority support",
      purchase: "Purchase",
      planStarter: "Starter",
      planPro: "Pro",
      planBusiness: "Business",
      featStore: "Monthly credit bundle",
      featBatch: "Batch image generation",
      featSupport: "Priority support",
      featBatchStarter: "Basic batch generation",
      featBatchPro: "Advanced batch generation",
      featBatchBusiness: "High-volume batch generation",
      featSupportStarter: "Standard support (48h)",
      featSupportPro: "Priority support (12h)",
      featSupportBusiness: "Priority support + success manager",
      featExportsStarter: "HD image downloads",
      featExportsPro: "HD + 4K image downloads",
      featExportsBusiness: "Brand asset kit exports"
    },

    // FAQ Section
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our platform.",
      contact: "Contact Support",
      q1: "Will AI change my product information?",
      a1: "No. We optimize from your source data and you can review or edit every field before publishing.",
      q2: "Do I need to install a plugin or connect my store?",
      a2: "No. Paste a product link to try it. Store authorization is only required when publishing or syncing.",
      q3: "Is my data and images secure?",
      a3: "Enterprise-grade encryption and isolation keep your data safe. Generated images include invisible watermarks and forensic proof.",
      q4: "How does the free trial and pricing work?",
      a4: "New users get one full task with only one hero image. After that, choose a subscription or credits with transparent pricing."
    },

    // CTA Section
    cta: {
      title: "Ready to Scale",
      titleBreak: "Your E-commerce Business?",
      subtitle: "Join thousands of sellers already using AI to dominate global marketplaces.",
      button: "Get Started Free"
    },

    // Footer
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      support: "Support",
      rights: "2024 OneClickSell. All rights reserved."
    },

    // Trust page
    trust: {
      forensicData: "Forensic Metadata",
      badge: "Enterprise Security",
      title: "Protect Your",
      titleHighlight: "Digital Assets",
      subtitle: "Military-grade security for your product images and brand identity.",
      dmcaBadge: "DMCA Protected",
      forensicBadge: "Forensic Ready",
      encryptedBadge: "256-bit Encrypted",
      nav: {
        technology: "Technology",
        legal: "Legal Framework",
        whitepaper: "Resources"
      },
      tech1Title: "Neural Watermarking",
      tech1Desc: "Invisible markers that survive any transformation.",
      tech2Title: "Blockchain Timestamp",
      tech2Desc: "Immutable proof of creation on distributed ledger.",
      tech3Title: "AI Detection",
      tech3Desc: "Real-time monitoring across all marketplaces.",
      compareTitle: "See the Difference",
      compareSub: "Move the slider to reveal hidden watermarks.",
      rawOutput: "Raw Output",
      status: "Status",
      ctaBtn: "Start Protection",
      protectedLayer: "Protected Layer",
      frameworkTitle: "Legal Framework",
      frameworkSub: "Compliance-ready documentation and verified evidence trails.",
      licenseTitle: "License Agreement",
      licenseDesc: "Instantly generate licensing documentation with immutable proof.",
      dmcaPackageTitle: "DMCA Package",
      dmcaPackageDesc: "Auto-build takedown evidence packages for marketplaces.",
      complianceTitle: "Compliance Ready",
      complianceDesc: "Structured evidence that meets platform enforcement requirements.",
      platformLabel: "Platform",
      priority: "Priority",
      refLabel: "Reference ID",
      evidenceLabel: "Evidence",
      evidence1: "Original listing screenshots",
      evidence2: "Watermark verification hash",
      generatorTitle: "Automated DMCA Generator",
      generatorSub: "Generate enforcement documents in minutes.",
      generatorNote: "Compatible with Amazon, Shopify, Etsy, and 30+ platforms.",
      letterAutomation: "Auto-generated DMCA Notice",
      letterBody1: "This notice confirms ownership and infringement evidence.",
      letterBody2: "All metadata and timestamps are verified on-chain.",
      letterBody3: "Supporting evidence is attached in the case file.",
      letterBody4: "Please remove the infringing content immediately.",
      letterNotice: "This notice is generated for immediate submission.",
      letterSign: "Authorized Representative",
      ctaTitle: "Ready to Protect Your Brand?",
      ctaTitleBreak: "Start Enforcement Today",
      ctaSub: "Launch compliance-grade protection in under 5 minutes.",
      downloadBtn: "Download Evidence Pack",
      verifiedHash: "Verified Hash",
      urlLabel: "Infringing URL",
      selectPlatform: "Select Platform",
      toLabel: "To",
      rights: "All rights reserved"
    },

    // Auth translations
    welcome: "Welcome Back",
    loginSubtitle: "Please enter your details to sign in to your professional account.",
    smsMethod: "SMS Code",
    passMethod: "Password",
    phoneLabel: "Phone Number",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "Mobile number",
    phoneRequired: "Please enter your phone number.",
    phoneInvalid: "Invalid phone number.",
    sendCode: "Send Verification Code",
    sendCodeFailed: "Failed to send the code. Please try again.",
    debugCodeHint: "Development code: {code}",
    verifyLabel: "Verification Code",
    resendIn: "Resend in {time}s",
    resendNow: "Resend Code",
    invalidCode: "Invalid verification code. Try 123456.",  // pragma: allowlist secret
    loginFailed: "Login failed. Please try again.",
    passwordLabel: "Password",
    forgotPass: "Forgot Password?",
    passPlaceholder: "Enter your password",
    passwordRequired: "Please enter your password.",
    passwordTooShort: "Password must be at least 8 characters.",
    signIn: "Sign In",
    newToApp: "New to OneClickSell?",
    signUp: "Sign up",
    createAccount: "Create Your Account",
    signupSubtitle: "Join 5,000+ sellers optimizing their listings.",
    codeVerified: "Code Verified",
    setPassPlaceholder: "Set your password",
    agree: "I agree to the",
    userAgreement: "User Agreement",
    and: "and",
    privacyPolicy: "Privacy Policy",
    getStarted: "Get Started for Free",
    haveAccount: "Already have an account?",
    login: "Log in",
    resetTitle: "Reset Your Password",
    resetSubtitle: "Enter your mobile number to receive a verification code.",
    sendReset: "Send Reset Code",
    resetStep1: "Verify Phone",
    resetStep2: "Enter Code",
    resetStep3: "Set New Password",
    resetCodeSent: "Verification code sent. Please check your phone.",
    resetCodeHint: "Enter the 6-digit code",
    resetVerifyAction: "Verify & Continue",
    resetBackStep: "Back to Previous Step",
    resetMismatch: "Passwords do not match.",
    resetRequirement: "Use at least 8 characters with letters and numbers.",
    resetSuccess: "Password updated successfully.",
    resetSuccessHint: "Returning to sign in...",
    resetContextMissing: "Reset session expired. Please request a new code.",
    resetFailed: "Password reset failed. Please try again.",
    resetStrengthLabel: "Strength: {level}",
    resetStrengthWeak: "Weak",
    resetStrengthMedium: "Medium",
    resetStrengthStrong: "Strong",
    backLogin: "Back to Login",
    phoneVerified: "Phone Verified",
    setNewPass: "Set New Password",
    newPassSubtitle: "Please create a strong password to secure your account.",
    newPassLabel: "New Password",
    newPassPlaceholder: "Create a new password",
    strength: "Strength: Strong",
    confirmPassLabel: "Confirm New Password",
    confirmPassPlaceholder: "Repeat new password",
    updatePass: "Update Password",
    backSignIn: "Back to Sign In",
    captchaLabel: "Human Verification",
    captchaHint: "Complete the verification to continue.",
    captchaLoading: "Loading verification...",
    captchaRequired: "Please complete the verification first.",
    captchaUnavailable: "Verification is unavailable. Please try again later.",
    captchaLoadFailed: "Verification failed to load. Refresh and try again.",
    captchaError: "Verification failed. Please retry.",

    // Profile Menu
    profile: {
      proPlan: "Pro Plan",
      freePlan: "Free Plan",
      guest: "Guest",
      coreNav: "Core Navigation",
      workspace: "Workspace",
      assetMgmt: "Asset Management",
      copyright: "Copyright Assets",
      pricing: "Pricing Strategy",
      storeAuth: "Store Authorization",
      marketIntel: "Market Intelligence",
      beta: "Beta",
      account: "Account",
      settings: "Account Settings",
      billing: "Billing & Credits",
      help: "Help Center",
      signOut: "Sign Out"
    },

    // Settings
    settings: {
      general: "General Info",
      security: "Security",
      preferences: "Platform Preferences",
      billing: "Billing & Credits",
      notifications: "Notifications",
      integrations: "Integrations",
      storeAuth: "Store Authorization"
    },

    // Store Binding
    storeBind: {
      title: "Store Authorization",
      subtitle: "Link your marketplace stores to enable direct AI publishing.",
      guideTitle: "Setup Guide",
      step1Title: "Login to Ozon",
      step1Desc: "Access your account at the Ozon Seller Center portal to begin the integration.",
      step1Btn: "Go to Ozon Seller Center",
      step2Title: "API Settings",
      step2Desc: "Navigate to Settings > API Keys in the top menu.",
      step2Btn: "Open API Settings",
      step3Title: "Generate Admin Key",
      step3Desc: "Select \"Admin\" key type, generate it, and copy the Client ID and Key for use in the form.",

      // Store Switcher
      activeSwitcher: "Active Store Switcher",
      manageAll: "Manage All Stores",
      currentlyActive: "Currently Active",
      synced: "Synced",
      connected: "Connected",
      uploads7d: "7d Uploads",
      connectNew: "Connect New Store",

      // Platform Selection
      fullApiIntegration: "Full API Integration",
      readyConnect: "Ready to connect",
      comingSoon: "Coming Soon",
      active: "Active",

      // Connection Form
      connectOzon: "Connect Ozon Store",
      statusLabel: "Status",
      authorized: "Authorized & Ready",
      noStoresLinked: "No stores linked",
      howToGetKey: "How to get API Key?",
      storeName: "Store Name",
      storeNamePlaceholder: "e.g., My Ozon Shop",
      clientId: "Client ID",
      clientIdPlaceholder: "Enter Client ID",
      apiKey: "API Key",
      addStore: "Add Ozon Store",
      testConnection: "Test Connection",
      testing: "Testing...",

      // Syncing
      syncingData: "Syncing Store Data...",
      syncingDesc: "Importing categories, shipping methods, and warehouse info from Ozon. This will only take a moment.",
      syncComplete: "Synchronization Complete",
      syncCompleteDesc: "All categories and warehouse data have been successfully synced. Your store is now ready for AI listing generation.",
      success: "Success",

      // Success State
      startListing: "Start Listing Generation",
      connectionSuccess: "Connection Successful",

      // Security
      securityTitle: "API Connection Security",
      securityEncryption: "Your credentials are stored using AES-256 military-grade encryption.",
      securityNoPassword: "OneClickSell will never ask for or store your marketplace login password.",

      // Table
      authorizedStores: "Authorized Stores",
      platform: "Platform",
      actions: "Actions",
      syncSettings: "Sync Settings",
      items: "items",

      // Footer
      storageSystem: "Storage System: Localized AES Encryption",
      securityGuaranteed: "Security Guaranteed",
      encryptedBridge: "Encrypted API Bridge",
      noPasswordStorage: "No Password Storage",
      cancel: "Cancel",
      saveAuth: "Save Authorization",

      // Toast
      toastTitle: "Ozon Store Ready",
      toastDesc: "All categories and warehouse data have been successfully synced."
    },

    // Capability Settings
    capability: {
      title: "Capability Settings",
      subtitle: "Configure platform-specific AI orchestration. Direct integration status varies by market.",
      targetPlatform: "Target Platform Selection",
      selectOzon: "Select Ozon for Full Automation",
      supported: "Fully Supported",
      draftOnly: "Draft Only",
      listingPrep: "Listing Prep",
      systemNotice: "System Notice",
      systemNoticeBody: "Full automation (Direct Upload) is currently exclusive to Ozon. Other platforms support AI Content Generation (Images + Copy + Basic Specs) with manual upload.",
      platformActive: "Ozon Active",
      platformOzonDesc: "Optimized for CIS market & regional logistics",
      platformAmazonDesc: "Amazon support is coming, currently listing prep only.",
      comingSoon: "Coming Soon",
      targetMarket: "Target Market",
      russiaDefault: "Russia (Default for Ozon)",
      outputLanguage: "Output Language",
      russianDefault: "Russian (Standard Cyrillic)",
      aiStrategy: "AI Content Strategy",
      stratConversion: "Conversion Focused",
      stratProfessional: "Professional",
      stratMinimalist: "Minimalist",
      stratSeo: "SEO-Heavy",
      strategyTier: "Strategy Tier",
      strategyEconomy: "Cost Focused",
      strategyEconomyDesc: "Lower cost, faster turnaround, essential optimizations.",
      strategyBalanced: "Balanced",
      strategyBalancedDesc: "Recommended balance of cost, speed, and quality.",
      strategyPremium: "Premium Quality",
      strategyPremiumDesc: "Maximum quality with deeper optimization.",
      brandGuardTitle: "Brand & Compliance Guard",
      brandGuardDesc: "Apply brand protection and compliance safeguards across content and images.",
      brandGuardHint: "Advanced protection remains managed by the platform.",
      summaryTitle: "Execution Summary",
      summaryHint: "What will be applied",
      summaryImages: "Generate {count} images (main + scenario + feature).",
      summarySeo: "SEO depth: {level}.",
      summaryRich: "Rich content theme: {theme}.",
      summaryBrandOn: "Brand guard enabled.",
      summaryBrandOff: "Brand guard disabled.",
      summaryDegradedAmazon: "Amazon rich content unavailable, downgraded to listing prep.",
      saveDefault: "Save as Default",
      useDefault: "Use Default",
      saving: "Saving...",
      saved: "Saved",
      aiContentGen: "AI Content Generation",
      aiContentDesc: "Images, titles, and localized specifications",
      active: "ACTIVE",
      directPublish: "One-Click Direct Publish",
      directPublishDesc: "Bypass manual CSV export/import",
      ozonExclusive: "OZON EXCLUSIVE",
      launchBtn: "Launch AI Orchestration",
      status: "System status: Ready for Ozon high-speed synchronization",
      secure: "Secure Protocol",
      neural: "Neural Engine v4.2",
      apiSync: "API Sync Active"
    },

    aiConfig: {
      badge: "AI Configuration",
      pageTitle: "AI Optimization Settings",
      pageSubtitle: "Review and fine-tune the AI parameters before launching your optimization task.",
      statusHint: "AI suggestions ready for review",
      targetPlatform: "Target Platform",
      platformActive: "Ozon Active",
      platformOzonDesc: "Optimized for CIS market & regional logistics",
      platformAmazonDesc: "Global marketplace optimization pipeline",
      platformShopifyDesc: "Listing prep for Shopify storefronts",
      platformAliDesc: "Cross-border listing preparation",
      comingSoon: "Coming Soon",
      targetLanguage: "Target Language",
      languageRu: "Russian",
      languageEn: "English",
      contentStyle: "Content Style",
      styleProfessional: "Professional",
      styleCreative: "Creative",
      styleConcise: "Concise",
      styleCustom: "Custom",
      stylePrompt: "Style Prompt Logic",
      currentStyle: "Current Style:",
      customActive: "Custom Logic Active",
      promptProfessional: "Prompt: Focus on technical specifications, durability, and business-formal tone.",
      promptCreative: "Prompt: Use evocative language, emotional storytelling, and lifestyle benefits.",
      promptConcise: "Prompt: Direct, punchy sentences focusing on key value propositions.",
      promptCustom: "Custom prompt loaded. Review advanced settings for final adjustments.",
      customPrompt: "Custom Prompt Editor",
      customPromptPlaceholder: "Write your custom prompt here...",
      customPromptHint: "Advanced variables: {product_name}, {category}, {specs}",
      customHint: "Open advanced editor to refine custom prompt",
      imageGeneration: "Enable AI Image Generation",
      imageGenerationDesc: "Removes watermarks and creates custom lifestyle backgrounds.",
      imageCredits: "Image generation will consume image credits per product.",
      trialImageBadge: "Trial: 1 main image only",
      trialImageHint: "Trial mode generates 1 main image. Upgrade to unlock full image sets.",
      trialUpgradeCta: "View plans",
      seoLevel: "SEO Optimization Level",
      seoStandard: "Standard",
      seoStandardDesc: "Basic keyword injection and meta-tag optimization",
      seoDeep: "Deep Analysis",
      seoDeepDesc: "Semantic clustering, competitor analysis, and LSI keyword integration",
      recommended: "Recommended",
      launch: "Launch AI Optimization",
      modalTitle: "AI Optimization Configuration",
      modalSubtitle: "Set your final preferences before launching AI agents.",
      useDefault: "Use Default Configuration",
      advanced: "Advanced Settings",
      cancel: "Cancel",
      saveDraft: "Save Draft",
      saving: "Saving...",
      saved: "Saved",
      footerHint: "AI agents will follow your selected strategy and credits plan."
    },

    // AI Workspace / Review
    ai: {
      workspaceTitle: "Active Listing Optimization",
      syncProgress: "Sync Progress",
      sourceRef: "Source Reference",
      rawMedia: "Raw Media Assets",
      brandProt: "Brand Protection",
      agentOrch: "Agent Orchestration",
      neuralCenter: "Neural Insight Center",

      // Modules
      sourceAnalysis: "Source Analysis & Structure",
      byImageAnalyzer: "By Image Analyzer",
      dataStructurer: "Data Structurer",
      rawImages: "Raw Supplier Images",
      scanned: "Scanned",
      unstructured: "Unstructured Text → Structured Attributes",
      processed: "Processed",

      seoArch: "SEO Keyword Graph",
      bySeo: "By SEO Architect",
      optTitle: "Optimized Title",
      score: "Score",
      bullets: "Bullet Points",
      keywordStrat: "Keyword Strategy",
      core: "Core",
      hot: "Hot",
      longTail: "Long-Tail",

      visualPipeline: "Visual Asset Pipeline",
      byImgSel: "By Image Selector",
      visualPlanner: "Visual Planner",
      refSel: "Reference Selection",
      sourcesLocked: "Sources Locked",
      visualPlan: "Visual Plan (Generative Prompts)",
      readyArtist: "Ready for Artist",

      optGallery: "Listing Writer",
      byArtist: "By Visual Artist",

      marketInt: "Platform Adapter",
      byRichBuilder: "By Rich Content Builder",
      contextMaster: "Context Master",
      aplusPreview: "Rich Content Preview",
      fieldMap: "Marketplace Field Mapping",

      motionProd: "Export & Delivery",
      byVideoCreator: "By Video Creator",
      rendering: "VEO 3.1 Rendering Engine",
      scriptGen: "Script Generated",

      // Agents
      analyzer: "Product Understanding",
      structurer: "Product Understanding",
      architect: "SEO Keyword Agent",
      planner: "Visual Strategy",
      selector: "Visual Strategy",
      richContent: "Platform Adapter",
      videoCreator: "Export Agent",
      visualArtist: "Listing Writer",

      // Status
      complete: "COMPLETE",
      thinking: "THINKING",
      queued: "QUEUED",
      waiting: "WAITING",
      idle: "IDLE",
      active: "ACTIVE (RENDERING)",
      enableVideo: "Enable Video Generation",

      // Footer
      reviewChanges: "Review Changes",
      oneClick: "One-Click Publish",
      neuralActive: "Neural Insight Center active. Pipeline targeting Amazon US Marketplace."
    },

    studio: {
      control: {
        title: "Generation & Debug Control",
        collection: "Collection:",
        collectionEmpty: "Not selected",
        marketStatus: "Market Status",
        skipImages: "Skip images",
        start: "Start generation",
        starting: "Starting...",
        stageDebug: "Single-stage debug",
        runStage: "Run stage",
        executing: "Running..."
      },
      errors: {
        missingCollection: "Missing collection ID for Studio.",
        previewFailed: "Failed to load preview data.",
        stageStatusFailed: "Failed to load stage status.",
        generateFailed: "Failed to start generation.",
        stageExecuteFailed: "Failed to run stage.",
        agentLogFailed: "Failed to load agent logs.",
        previewRequestFailed: "Preview request failed (status {status}).",
        imagePlanRequestFailed: "Image plan request failed (status {status}).",
        stageStatusRequestFailed: "Stage status request failed (status {status}).",
        generateRequestFailed: "Generate request failed (status {status}).",
        stageExecuteRequestFailed: "Stage execution request failed (status {status}).",
        agentLogRequestFailed: "Agent logs request failed (status {status}).",
        promptUpdateFailed: "Failed to update prompt.",
        imageGenerateFailed: "Failed to generate image.",
        imageRegenerateFailed: "Failed to regenerate image.",
        stageFailed: "Stage failed: {stage}"
      },
      action: {
        generateStarted: "Generation started.",
        stageTriggered: "Stage triggered: {stage}",
        promptUpdated: "Prompt updated for image {index}.",
        imageGenerateTriggered: "Image generation triggered: {index}",
        imageRegenerateTriggered: "Image regeneration triggered: {index}"
      },
      stage: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Graph",
        listing: "Listing Writer",
        visual: "Visual Strategy",
        adapter: "Platform Adapter",
        export: "Export & Delivery"
      },
      market: {
        medium: "Medium Competition",
        high: "High Competition",
        low: "Low Competition"
      },
      source: {
        title: "Product Understanding",
        verified: "Data Verified",
        pending: "Awaiting Data",
        visualDiagnostics: "Asset Diagnostics",
        analyzing: "Analyzing",
        ready: "Ready",
        summary: "Summary",
        expand: "Expand details",
        collapse: "Collapse details",
        thumbnailAlt: "Thumbnail",
        mainImageAlt: "Source Image",
        emptyImages: "No source images",
        emptyTitle: "Waiting for source images",
        emptyDesc: "Connect the extension or import a product to start understanding.",
        imageCount: "Images: {count}",
        noImages: "No images",
        scanning: "ANALYZING...",
        sectionsTitle: "Captured Sections",
        sectionsEmpty: "No section data yet."
      },
      rawText: {
        title: "Source Text",
        length: "{count} chars",
        emptyTitle: "No raw text yet",
        emptyDesc: "Run product understanding to parse source text."
      },
      productCore: {
        title: "Product Core",
        materials: "Materials",
        specs: "Specs",
        useCases: "Use cases",
        audience: "Audience",
        facts: "Facts",
        emptyTitle: "Product core pending",
        emptyDesc: "Run product understanding to generate core facts."
      },
      productSchema: {
        title: "Product Data Schema",
        identity: "Identity",
        titleLabel: "Title",
        brandLabel: "Brand",
        categoryLabel: "Category",
        attributes: "Key Attributes",
        empty: "No attribute data yet.",
        emptyTitle: "Attributes pending",
        emptyDesc: "Complete structuring to generate product attributes.",
        logistics: "Logistics",
        price: "Price",
        weight: "Weight",
        dimensions: "Dimensions",
        emptyValue: "—",
        sku: "SKU Overview",
        skuCount: "Variants",
        skuUnit: "Items",
        noColors: "No color data",
        sizes: "Sizes",
        noSizes: "No size data"
      },
      metrics: {
        images: "Images",
        skus: "SKUs",
        attributes: "Attributes",
        sections: "Sections",
        rawText: "Raw Text"
      },
      imageText: {
        title: "Image Text Extraction",
        count: "Images {count}",
        empty: "No image text extracted yet.",
        emptyTitle: "No text detected yet",
        emptyDesc: "Run image text extraction to populate results here."
      },
      seo: {
        strength: "Search Strength",
        failed: "SEO generation failed",
        failedDesc: "No SEO result available.",
        optimizedKeywords: "Optimized Keywords",
        emptyKeywords: "No keywords generated yet.",
        emptyTitle: "SEO keywords pending",
        emptyDesc: "Run SEO stage to generate keyword graph.",
        searchVolume: "Search Volume",
        competition: "Intent",
        unknown: "Unknown",
        clusterLabel: "Cluster",
        volumeTier: "Volume",
        volumeHigh: "High",
        volumeMedium: "Medium",
        volumeLow: "Low",
        volumeUnknown: "Unknown",
        searchPipeline: "Keyword Graph Summary",
        pipelinePending: "Generating keyword graph...",
        graphTitle: "Keyword Graph Visualization",
        graphEmpty: "No graph nodes yet.",
        coverageTitle: "Coverage Report",
        missingAspects: "Missing Aspects",
        duplicates: "Duplicate Keywords",
        languageIssues: "Language Issues",
        coverageEmpty: "No issues detected"
      },
      keyword: {
        primary: "Primary",
        secondary: "Secondary",
        longTail: "Long-Tail",
        conversion: "Conversion",
        transactional: "Transactional",
        information: "Information",
        other: "Other"
      },
      agent: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Agent",
        listing: "Listing Writer",
        visual: "Visual Strategy",
        adapter: "Platform Adapter",
        export: "Export & Delivery",
        error: "Stage failed. Check logs."
      },
      visual: {
        title: "Visual Strategy",
        subtitle: "Prompt plans • {count} sequences",
        collapseAll: "Collapse all",
        empty: "No visual prompts yet. Run the visual strategy stage first.",
        emptyTitle: "Visual strategy ready to start",
        emptyDesc: "Run the visual strategy stage to generate prompt plans. We'll show editable prompts and image outputs here.",
        emptyStep1: "Step 1: Run visual strategy",
        emptyStep2: "Step 2: Review prompts",
        emptyStep3: "Step 3: Generate images",
        constraintsTitle: "Constraints",
        constraintsEmpty: "No constraints",
        keywordsTitle: "Keyword Focus",
        keywordsEmpty: "No keywords yet",
        sceneLabel: "Scene",
        placeholderMainTitle: "Hero shot",
        placeholderMainDesc: "Primary listing image prompt",
        placeholderSceneTitle: "Scenario shot",
        placeholderSceneDesc: "Lifestyle and usage context",
        placeholderDetailTitle: "Detail shot",
        placeholderDetailDesc: "Material and feature close-ups",
        trackPrimary: "Track 01: Core sequence",
        trackSecondary: "Track 02: Detail sequence",
        collapse: "Collapse",
        viewMore: "View {count} more",
        statusWaiting: "Status: Waiting",
        statusGenerated: "Status: {current}/{total} Generated",
        statusPrompted: "Status: {count} Planned",
        generated: "Generated",
        savePrompt: "Save Prompt",
        generateImage: "Generate Image",
        regenerate: "Regenerate",
        planBadge: "{index} {type}",
        planDefaultType: "MAIN",
        planTypeTitle: "{type} Plan",
        planTitle: "Image Plan {index}",
        planAspect: "Aspect {ratio}",
        planSubtitle: "Prompt plan"
      },
      listing: {
        title: "Listing Writer",
        writing: "Writing...",
        keywordInput: "SEO input keywords",
        keywordEmpty: "No keyword hints yet.",
        emptyTitle: "Listing pending",
        emptyDesc: "Run listing stage to generate the listing output.",
        fieldTitle: "Title",
        shortDesc: "Short description",
        longDesc: "Long description",
        bullets: "Bullet points",
        emptyBullets: "No bullet points yet.",
        faq: "FAQ",
        emptyFaq: "No FAQ yet."
      },
      adapter: {
        title: "Platform Adapter",
        platformUnknown: "Platform",
        summary: "Adapter Summary",
        platform: "Platform",
        format: "Format",
        fieldCount: "Fields",
        category: "Category",
        typeId: "Type ID",
        price: "Price",
        images: "Images",
        attributesCount: "Attributes",
        richContent: "Rich content",
        missingFields: "Missing fields",
        missingFieldsEmpty: "No missing fields",
        payload: "Adapter Output",
        emptyPayload: "No adapter output.",
        fieldMap: "Field Mapping",
        emptyMappingTitle: "No mapped fields yet",
        emptyMappingDesc: "Run adapter stage to generate platform payload."
      },
      export: {
        title: "Export & Delivery",
        exporting: "Exporting...",
        platform: "Platform",
        category: "Category",
        columns: "Columns",
        rows: "Rows",
        templateKey: "Template key",
        format: "Format",
        version: "Template Version",
        validation: "Validation",
        validationPass: "Passed",
        validationPending: "Pending / Issues",
        fileName: "File Name",
        delivery: "Delivery",
        ready: "Download ready.",
        pending: "Waiting for export data.",
        download: "Download",
        waiting: "No download link yet.",
        validationErrors: "Validation errors"
      },
      gallery: {
        main: "MAIN",
        empty: "No generated images",
        slot: "SLOT {index}",
        diffusing: "DIFFUSING...",
        mainPlaceholder: "Hero slot",
        emptySlot: "Slot {index}",
        emptyHint: "Awaiting render",
        waiting: "Generating..."
      },
      marketplace: {
        emptyRich: "No rich content assets yet",
        widgets: "Widgets {count}",
        images: "Images {count}",
        theme: "Theme {theme}",
        emptyMapping: "No mapped attributes yet."
      },
      richContent: {
        title: "Rich Content Structure",
        summary: "{count} widgets",
        empty: "No rich content widgets yet.",
        emptyTitle: "Rich content is ready to render",
        emptyDesc: "Once the rich content stage finishes, widgets and images will appear here.",
        emptyPreview: "No rich content preview yet.",
        images: "Rich content images",
        emptyImages: "No rich content images available.",
        version: "Version",
        generatedAt: "Generated",
        blocks: "{count} blocks",
        rows: "{count} rows",
        columns: "{count} columns",
        untitled: "Untitled widget",
        noDescription: "No text content yet.",
        moreWidgets: "More widgets: {count}",
        emptyMappingTitle: "No field mapping yet",
        emptyMappingDesc: "Finish attribute generation to map fields automatically.",
        detailHint: "Structure ready",
        viewDetail: "View details",
        collapse: "Collapse",
        detailTitle: "Widget details",
        detailDesc: "Structured output from the rich content agent.",
        jsonTitle: "Widget JSON",
        blocksTitle: "Block preview",
        emptyBlocks: "No blocks generated yet.",
        galleryTitle: "Rich content image stream",
        emptyGallery: "No rich content images yet.",
        emptyGalleryDesc: "Generate rich content to populate the image stream.",
        widget: {
          showcase: "Showcase",
          text: "Text Block",
          table: "Table",
          video: "Video",
          list: "List",
          unknown: "Widget"
        }
      },
      video: {
        empty: "Video generation is not enabled yet.",
        hint: "Enable the video stage to produce motion assets.",
        recording: "RECORDING..."
      },
      status: {
        complete: "COMPLETE",
        thinking: "THINKING...",
        processing: "PROCESSING...",
        error: "ERROR",
        waiting: "WAITING"
      },
      agentLogs: {
        title: "Agent Live Feed",
        subtitle: "Agent Stream Console",
        chainTitle: "AGENT LOG STREAM",
        streaming: "Streaming",
        emptyTitle: "No agent logs yet",
        emptyDesc: "Run a stage to see live agent events here.",
        logCount: "{count} logs",
        lastUpdated: "Last updated {time}",
        duration: "Duration {duration}",
        expandLog: "Expand",
        collapseLog: "Collapse",
        status: {
          running: "RUNNING",
          completed: "DONE",
          failed: "FAILED",
          idle: "IDLE"
        },
        event: {
          start: "START",
          thinking: "THINK",
          result: "RESULT",
          error: "ERROR",
          cacheHit: "CACHE",
          skip: "SKIP"
        }
      }
    },

    // Pricing Engine
    pricingEngine: {
      title: "OneClickSell Dynamic Pricing Engine",
      subtitle: "Multi-platform profit calculator with platform-specific fee logic for global marketplaces.",
      calcMode: "Calculation Mode",
      manualEntry: "Manual Entry",
      myListings: "My Listings",
      destPlatform: "Destination Platform",
      globalChannels: "{count} Global Platforms Available",
      ready: "Ready",
      soon: "Soon",
      listingsOzonOnly: "Listings sync is currently available for Ozon only.",
      inputMode: "Input Mode",
      compactMode: "Compact",
      fullMode: "Advanced",
      singlePlatformHint: "Most sellers operate on a single platform.",
      rateSource: "Live FX source",
      rateLoading: "Updating...",
      rateUpdated: "Updated {date}",
      rateError: "Rate error",
      rateErrorDesc: "Failed to fetch the latest exchange rate. Please retry.",
      autoRateOn: "Auto Rate",
      autoRateOff: "Manual",
      refreshRate: "Refresh Rate",
      autoRateHint: "Auto rate enabled (Frankfurter API).",
      manualRateHint: "Manual override enabled.",
      tiktokRegion: "Selling Region",
      regionUs: "United States",
      regionUk: "United Kingdom",
      regionSea: "Southeast Asia",
      tiktokRegionHint: "Switching region will update commission and transaction presets. Edit any field if your actual rate differs.",
      selectProduct: "Select Product from Workspace",
      viewDetails: "View Details",
      dataSynced: "Data Synced",
      pricingStrategy: "Pricing Strategy",
      targetMargin: "Target Margin (%)",
      targetProfit: "Target Profit (¥)",
      targetValue: "Target Value",
      breakEven: "Break-even",
      suggestedPrice: "Suggested Listing Price",
      rate: "Rate: 1 CNY = {rate} {currency} (Input)",
      breakEvenPrice: "Break-even Price: {price}",
      optimizedMsg: "Optimized for {value}% Margin",
      optimizedProfit: "Optimized for {value} Profit",
      netProfit: "Net Profit",
      profitMargin: "Profit Margin",
      targetMet: "Target Met",
      roi: "ROI",
      highMargin: "High Margin",
      updateListing: "Update Price in Listing",
      syncDesc: "Instantly sync this price to your Ozon listing in the workspace.",
      syncSuccess: "Price Sync Complete",
      syncSuccessDesc: "Successfully sent {price} to {platform} listing.",
      feeBreakdown: "Platform Fee Breakdown",
      catLogic: "Category Logic Applied",
      commission: "Marketplace Commission",
      lastMile: "Last-mile Delivery",
      handling: "Handling & Warehousing",
      totalOpCost: "Total Operational Cost",
      costBreakdown: "Cost Breakdown",
      exportReport: "Export Report",
      shareImage: "Generate Share Image",
      shareReady: "Share Image Ready",
      shareReadyDesc: "Share image for {platform} saved to your device.",
      shareError: "Share Image Failed",
      shareErrorDesc: "Unable to render share image. Please retry.",
      saveScenario: "Save Scenario",
      prodMetadata: "Product Metadata (Locked)",
      prodCategory: "Product Category",
      purchaseCost: "Purchase Cost (CNY)",
      weight: "Weight (kg)",
      dimensions: "Dimensions (L*W*H cm)",
      logistics: "Logistics",
      air: "Air",
      sea: "Sea",
      saveWorkspace: "Save this calculation to your workspace",
      saveWorkspaceDesc: "Sync directly to Ozon and track your margin history by creating a free account.",
      createAccount: "Create Free Account",
      basicCosts: "Base Costs",
      costCurrency: "Cost currency: CNY → {currency}",
      purchaseCostLabel: "Purchase Cost",
      shippingCostLabel: "Shipping Cost",
      packagingCostLabel: "Packaging / Prep Cost",
      otherCostLabel: "Other Cost",
      shippingCharged: "Shipping Charged to Buyer",
      exchangeRateLabel: "Exchange Rate (1 CNY → {currency})",
      platformFees: "Platform Fee Inputs",
      categoryLabel: "Category / Segment",
      referralFeeLabel: "Referral Fee",
      referralRateLabel: "Referral / Commission Rate",
      referralMinFeeLabel: "Minimum Referral Fee",
      sellingPlan: "Selling Plan",
      sellingPlanIndividual: "Individual",
      sellingPlanProfessional: "Professional",
      sellingPlanFee: "Per-item Selling Fee",
      closingFeeLabel: "Closing Fee",
      fulfillmentMode: "Fulfillment Mode",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "Fulfillment Fee",
      storageFee: "Storage Fee",
      inboundShippingFee: "Inbound Shipping Fee",
      fbmHandlingFee: "FBM Handling / Pick Fee",
      platformDiscount: "Platform Discount",
      tax: "Tax / VAT",
      sellerDiscount: "Seller Discount",
      affiliateRate: "Affiliate Fee Rate",
      affiliatePartnerRate: "Affiliate Partner Rate",
      adsRate: "Ads / Promotion Rate",
      shippingType: "Shipping Option",
      shippingPlatform: "Platform Shipping",
      shippingSeller: "Seller Shipping",
      shippingFbt: "FBT (Fulfilled by TikTok)",
      fbtFulfillmentFee: "FBT Fulfillment Fee",
      fbtStorageFee: "FBT Storage Fee",
      transactionFeeRate: "Transaction Fee Rate",
      transactionFeeFixed: "Transaction Fixed Fee",
      serviceFeeRate: "Service Fee Rate",
      serviceFeeFixed: "Service Fixed Fee",
      orderServiceFee: "Order Service Fee",
      platformSupportFee: "Platform Support Fee",
      platformSupportFeeHint: "Some markets charge a fixed support fee per order (e.g., MY).",
      shippingSubsidy: "Shipping Subsidy / Program Cost",
      ozonPlacementFee: "Placement / Service Fee",
      ebayFinalValueRate: "Final Value Fee Rate",
      ebayPerOrderFee: "Per-order Fixed Fee",
      ebayInsertionFee: "Insertion Fee",
      ebayInternationalRate: "International Fee Rate",
      etsyListingFee: "Listing Fee",
      etsyTransactionFee: "Transaction Fee Rate",
      etsyPaymentRate: "Payment Processing Rate",
      etsyPaymentFixed: "Payment Fixed Fee",
      etsyOffsiteAdsRate: "Offsite Ads Rate",
      totalCost: "Total Cost",
      platformFeeTotal: "Platform Fees",
      baseCostTotal: "Base Cost",
      platformInsights: "Platform Insights",
      marketingCosts: "Marketing & Creator Costs",
      temuProgram: "Temu Incentives",
      aliexpressProgram: "AliExpress Campaigns",
      shopeeProgram: "Shopee Programs",
      temuHeroTitle: "Temu Low Commission Stack",
      temuHeroDesc: "Commission is often 0%. Track service fees, fulfillment, and subsidies separately.",
      temuHeroTag1: "Zero Commission",
      temuHeroTag2: "Service Fee Adjustable",
      aliexpressHeroTitle: "AliExpress Category Commission",
      aliexpressHeroDesc: "Commission varies by category. Use this panel to model payment fees and campaigns.",
      aliexpressHeroTag1: "5-8% Commission",
      aliexpressHeroTag2: "No Annual Fee",
      shopeeHeroTitle: "Shopee Fee Stack",
      shopeeHeroDesc: "Combine commission, transaction, and optional program fees.",
      shopeeHeroTag1: "Multi-fee",
      shopeeHeroTag2: "Program Optional",
      shopeeHeroTag3: "Support Fee",
      amazonHighlightTitle: "Amazon Plan & Fulfillment Snapshot",
      amazonHighlightDesc: "Model referral fees, plan charges, and FBA/FBM costs together to find the right floor price.",
      ebayHighlightTitle: "eBay Final Value Fee Focus",
      ebayHighlightDesc: "Final value fees apply to item + shipping + tax; add per-order and international fees if applicable.",
      walmartHighlightTitle: "Walmart Referral Fee Focus",
      walmartHighlightDesc: "Referral fee is category-based; WFS and other fulfillment costs can be added separately.",
      etsyHighlightTitle: "Etsy Listing Cycle Snapshot",
      etsyHighlightDesc: "Listing fee, transaction fee, payment processing, and offsite ads can be toggled for each item.",
      platformNotesAmazon: "Includes referral fee (category-based), per-item fee (Individual plan), and fulfillment fees (FBA/FBM).",
      platformNotesTikTok: "Fees may include commission (category-based), transaction fee, affiliate commission, and optional FBT fulfillment costs.",
      platformNotesOzon: "Commission rate depends on category; last-mile and handling are estimated based on weight.",
      platformNotesEbay: "Final value fees apply to item + shipping + tax; per-order fee is added separately.",
      platformNotesWalmart: "Referral fee varies by category; other fulfillment costs are excluded in this quick calculator.",
      platformNotesEtsy: "Includes listing fee, transaction fee, payment processing fee, and optional offsite ads.",
      platformNotesTemu: "Temu commission can be 0%, while a small service fee or fulfillment cost may apply depending on program.",
      platformNotesAliExpress: "AliExpress commission varies by category; payment or order service fees may apply by region.",
      platformNotesShopee: "Shopee fees typically include commission, transaction fee, and optional program/support fees.",
      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    // Copyright
    copyright: {
      nav: "Copyright Assets",
      title: "Copyright & Legal Assets",
      subtitle: "One-stop evidence bundles for instant IP enforcement and copyright protection.",
      statusActive: "Legal Security Status: Active",
      protected: "Protected Listings",
      certified: "Certified Bundles",
      onChain: "On-Chain Proofs",
      ready: "Enforcement Ready",
      filter: "Filter",
      searchPlaceholder: "Search by product name or SKU...",
      table: {
        preview: "Asset Preview",
        product: "Product / Platform",
        status: "Certification Status",
        bundle: "Legal Evidence Bundle",
        actions: "Actions",
        download: "Download Full Legal Bundle",
        previewBtn: "Preview",
        synced: "Synced: ",
        certified: "Certified",
        docs: "High-Res Docs"
      },
      bundleNav: {
        back: "Back",
        details: "Bundle Details",
        downloadCurrent: "Download Current",
        downloadAll: "Download Full Evidence Pack (.zip)",
        contents: "Bundle Contents",
        letter: "Official Authorization Letter",
        psd: "Forensic PSD Source",
        watermark: "Neural Watermark Analysis",
        blockchain: "Blockchain Proof-of-Creation",
        dispute: "IP Dispute Template",
        securityHash: "Security Hash"
      },
      tabs: {
        docView: "DOCUMENT VIEW",
        metaDash: "METADATA DASHBOARD",
        xray: "X-RAY ANALYSIS"
      },
      letter: {
        title: "Platform Authorization Letter",
        seal: "OFFICIAL SEAL APPLIED",
        certTitle: "Certificate of Authenticity",
        certSub: "Official Legal Evidence Record",
        docId: "Document ID",
        date: "Date of Issuance",
        subject: "Subject Matter Identification",
        prodName: "Product Name",
        sku: "Asset SKU",
        jurisdiction: "Platform Jurisdiction",
        grade: "Evidence Grade",
        gradeValue: "Forensic Certified",
        body1: "This document constitutes formal legal evidence that the asset described above has been verified and registered within the OneClickSell Intellectual Property Protection Vault.",
        body2: "Verification includes forensic analysis of source PSD files, embedding of neural watermarks persistent through transformation, and timestamping on the public blockchain ledger.",
        officer: "Legal Compliance Officer",
        signature: "Digitally signed: ",
        footer1: "Secure Forensic Chain: #991-AUTH-Z",
        footer2: "Proprietary Evidence Format v4.2"
      },
      psd: {
        reportTitle: "Forensic Metadata Report",
        verified: "Metadata Verified",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 Layers",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "Technical Property",
        colValue: "Forensic Value",
        created: "Creation Timestamp",
        createdDesc: "File system origin date",
        fingerprint: "Neural Fingerprint (SHA-256)",
        fingerprintDesc: "Unique mathematical identity",
        author: "Author ID",
        authorDesc: "Verified creator signature",
        software: "Software Signature",
        softwareDesc: "Engine and build version",
        seed: "Embedded Watermark Seed",
        seedDesc: "Persistent neural tracer",
        engine: "Analysis Engine",
        integrity: "Integrity Check",
        secure: "100% SECURE"
      },
      watermark: {
        title: "Neural Watermark X-Ray",
        scanning: "Scanning Neural Layers...",
        status: "Status",
        detected: "NEURAL WATERMARK DETECTED",
        score: "Resilience Score",
        stable: "CRYPTO-STABLE",
        encodingTitle: "Neural Encoding",
        encodingDesc: "This watermark is embedded at the neural level during generative diffusion. Unlike traditional pixel overlays, this signature is part of the image's weights, making it resistant to AI-based erasure, cropping, or noise injection.",
        latentSeed: "Latent Seed",
        engineSig: "Engine Signature",
        inference: "Inference Time",
        confidence: "Scan Confidence"
      },
      blockchain: {
        title: "Blockchain Verification Certificate",
        subtitle: "Immutable Proof of Asset Origination",
        verified: "Verified On-Chain",
        network: "Network:",
        networkVal: "Arbitrum One / Ethereum Mainnet",
        txHash: "Transaction Hash (TxID)",
        signature: "Digital Signature (EdDSA)",
        block: "Block Height",
        timestamp: "Timestamp (UTC)",
        legalNoteTitle: "Legal Admissibility Note",
        legalNoteDesc: "This certificate provides a cryptographically secure timestamp and authorship record. The record is permanently anchored to the Arbitrum L2 network, inheriting the full security of the Ethereum settlement layer.",
        scan: "Scan to verify",
        verifStatus: "Verification Status",
        protocol: "OneClick-ChainLink Oracle v2",
        confirmed: "CONFIRMED",
        confirmations: "Node Confirmations",
        integrity: "Data Integrity",
        secure: "100% SECURE"
      },
      dispute: {
        preview: "PREVIEW DOCUMENT",
        settings: "TEMPLATE SETTINGS",
        language: "Language:",
        title: "IP Infringement Takedown Notice",
        subtitle: "Automated Legal Compliance Document",
        to: "To: Legal Compliance Department",
        ref: "Ref:",
        date: "Date:",
        s1Title: "1. Statement of Ownership",
        s1Body: "I, the undersigned, hereby declare under penalty of perjury that I am the authorized representative of the copyright owner for the intellectual property described herein. OneClickSell Inc. maintains exclusive rights to the proprietary digital assets associated with this claim.",
        s2Title: "2. Description of Infringement",
        s2Body: "We have identified unauthorized use of our copyrighted visual assets at the following location:",
        s2Url: "URL:",
        s2Desc: "This listing utilizes proprietary product imagery and creative layouts that are identical or substantially similar to our original works.",
        s3Title: "3. Evidence of Originality",
        s3Body: "The originality and creation date of the disputed assets are verified through the following cryptographic methods included in the attached Evidence Pack:",
        s3Item1: "Neural Watermark:",
        s3Item1Desc: " Invisible forensic markers embedded within the image data matching our master files.",
        s3Item2: "Blockchain Proof:",
        s3Item2Desc: " Immutable proof-of-creation recorded on the Arbitrum Mainnet.",
        s4Title: "4. Legal Declaration",
        s4Body: "I have a good faith belief that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law. The information in this notification is accurate. We request the immediate removal of the infringing material.",
        signature: "Authorized Signature",
        division: "IP Protection Division, OneClickSell Inc.",

        sidebar: {
          verifStatus: "Verification Status",
          link: "Infringing Link",
          linkDesc: "URL provided",
          psd: "Forensic PSD",
          psdDesc: "Layer data verified",
          evidence: "Additional Evidence",
          missing: "2 modules missing",
          prob: "Enforcement Probability",
          lowConf: "Low Confidence",
          guide: "Evidence Completion Guide",
          scan: "Neural Watermark Scan",
          boost30: "Missing: +30% boost",
          complete: "Complete Now",
          proof: "Blockchain Proof",
          boost22: "Missing: +22% boost",
          autoFiling: "Automated Filing",
          filingNote: "Filing not recommended until confidence is above 80%.",
          autoSubmit: "Auto-Submit Claim",
          processing: "Processing...",
          submitted: "Claim Submitted"
        }
      }
    },

    // Workspace
    workspace: {
      nav: "Workspace",
      heroTitle: "Create Your First AI Listing",
      heroSub: "Paste a link → AI Optimization → One-click Publishing. Transform product data in seconds.",
      returningLabel: "Welcome back",
      returningTitle: "Pick up where you left off",
      returningSub: "Your latest imports are ready. Continue polishing listings or jump into the task center.",
      startBtn: "Start Importing Products",
      taskCenterEntry: "Open Task Center",
      pasteLink: "Paste Link",
      pasteDesc: "Import from AliExpress, 1688, Amazon and more.",
      uploadMedia: "Upload Media",
      uploadDesc: "Drag & drop images, videos or ZIP files.",
      batchImport: "Batch Import",
      batchDesc: "Import products in bulk via CSV or multiple URLs.",
      recentImports: "Recent Imports",
      viewHistory: "View History",
      extRequired: "Chrome Extension Required",
      extNotFound: "Not Found",
      extDesc: "Required for secure data collection from major platforms.",
      extDownload: "Download Extension",
      watchTutorial: "Watch Video Tutorial",
      readGuide: "Read User Guide",
      status: {
        processing: "Processing",
        ready: "Ready to Publish",
        uploaded: "Uploaded",
        failed: "Failed"
      },
      loadFailed: "Failed to load recent imports.",
      loadHint: "Please retry to fetch your latest tasks.",
      retry: "Retry",
      emptyTitle: "No imports yet",
      emptyDesc: "Start by importing a product link to see progress here.",
      loginTitle: "Log in to view your imports",
      loginDesc: "Sign in to sync your import history and workflow status.",
      loginAction: "Log In",
      unknownSource: "Unknown Source"
    },

    // Task Center
    taskCenter: {
      nav: "Task Center",
      breadcrumb: {
        workspace: "Workspace",
        taskCenter: "Task Center"
      },
      title: "Task Center",
      subtitle: "Manage single and batch product AI workflows.",
      target: {
        label: "Target:",
        platform: "Amazon",
        region: "United States",
        language: "English"
      },
      sync: {
        label: "Sync Progress",
        value: "85%"
      },
      reviewChanges: "Review Changes",
      createTask: "Create New Task",
      dropdown: {
        singleTitle: "Single Product Task",
        singleDesc: "Import via a single URL",
        batchTitle: "Batch Import Task",
        batchDesc: "Import via multiple URLs or CSV"
      },
      table: {
        source: "Source",
        task: "Task Name / ID",
        status: "Status",
        progress: "AI Progress",
        createdAt: "Created At",
        action: "Action"
      },
      status: {
        active: "Active",
        completed: "Completed",
        waiting: "Waiting",
        idle: "Idle",
        collect_pending: "Waiting for capture",
        assigned: "Capture assigned",
        collecting: "Collecting",
        collect_uploading: "Uploading",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        pending_generate: "Waiting to generate",
        generating: "Generating",
        ready: "Ready to upload",
        uploaded: "Uploaded",
        generation_failed: "Generation failed"
      },
      batchLabel: "Batch",
      itemsLabel: "Items",
      progress: {
        processing: "Processing",
        copywriting: "AI Copywriting",
        inQueue: "In Queue"
      },
      stage: {
        collect_pending: "Waiting for capture",
        collect_assigned: "Plugin assigned",
        collect_preparing: "Preparing capture",
        collect_scrolling: "Scrolling page",
        collect_collecting: "Collecting data",
        collect_saving: "Saving data",
        collect_uploading: "Uploading assets",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        generate_pending: "Waiting to generate",
        generate_info_structure: "Product understanding",
        generate_seo: "SEO keyword graph",
        generate_listing: "Listing writing",
        generate_visual: "Visual strategy",
        generate_adapter: "Platform adapter",
        generate_export: "Export delivery",
        generate_ready: "Ready to upload",
        generate_uploaded: "Uploaded",
        generate_failed: "Generation failed"
      },
      actions: {
        workspace: "Workspace",
        preview: "Preview",
        generate: "Start AI",
        retry: "Retry",
        retryCollect: "Retry Capture",
        retryGenerate: "Retry Generate",
        pending: "Pending"
      },
      tooltips: {
        previewLocked: "Preview is available after collection completes",
        previewUnavailable: "Preview requires a completed capture task",
        previewCollecting: "Capture in progress. Preview will be available soon.",
        previewFailed: "Capture failed. No preview data available.",
        expand: "Expand batch",
        collapse: "Collapse batch",
        errorFallback: "Task failed. Please retry or contact support.",
        workspaceLocked: "Workspace is available once capture and AI processing are complete"
      },
      errorSummaryDefault: "Task failed. Please retry.",
      pagination: {
        showing: "Showing 4 of 42 tasks"
      },
      banner: "Multi-agent pipeline active with Brand Protection. Targeting Amazon US. Workspace access available upon completion.",
      loading: "Loading tasks...",
      emptyTitle: "No tasks yet",
      emptyHint: "Import a product or start a batch to see progress here.",
      emptyAction: "Create Task",
      loginHint: "Log in to unlock Task Center tracking",
      loginDesc: "Sign in to review collection, AI generation, and upload progress.",
      loginAction: "Log In",
      loadFailed: "Failed to load tasks",
      retryHint: "Please refresh or try again later.",
      retryAction: "Reload",
      modal: {
        title: "Batch Import Task",
        subtitle: "Add multiple products to your catalog simultaneously.",
        supportedPlatforms: "Supported Platforms",
        productUrls: "Product URLs",
        totalLinks: "Total Unique Links:",
        onePerLine: "One URL per line",
        duplicateWarning: "3 duplicate links detected and automatically removed.",
        uploadTitle: "Upload CSV or Excel file",
        uploadSubtitle: "Max 500 items per batch. Ensure column A contains URLs.",
        uploadHint: "Need a template? Download our CSV/Excel example",
        browseFiles: "Browse Files",
        cancel: "Cancel",
        startBatch: "Start Batch Import"
      }
    },

    // Import
    import: {
      nav: "Import Product",
      title: "Import Product Source",
      subtitle: "Connect your supplier link or upload assets to begin AI optimization.",
      extActive: "Browser Extension Active",
      connected: "Connected",
      extActiveDesc: "The companion is active. You can import directly from suppliers.",
      extSettings: "Extension Settings",
      extMissing: "Extension Required",
      extMissingDesc: "Please install the companion to scrape product data locally.",
      downloadBtn: "Download Extension",
      downloadGuide: "Opening extension download...",
      urlLabel: "Product URL",
      urlSub: "Supports AliExpress, 1688, Amazon, Shopify, and more.",
      urlPlaceholder: "Paste your product link here...",
      urlRequired: "Please enter a product URL.",
      authRequired: "Please log in to start collection.",
      guestFailed: "Unable to start guest session. Please try again.",
      importBtn: "Import",
      submitting: "Submitting...",
      collectFailed: "Failed to submit collection task.",
      uploadTitle: "Upload Media Assets",
      uploadSub: "Drag and drop images, videos or ZIP folders here",
      chooseFiles: "Choose Files",
      alternative: "Alternative",
      targetGlobal: "Target: Global Market",
      taskCenterEntry: "Task Center",
      taskCenterHint: "Log in to track progress in Task Center.",
      recent: {
        title: "Recent Task",
        subtitle: "Latest import is processing",
        taskId: "Task ID",
        source: "Source URL",
        createdAt: "Created",
        statusHint: "We will notify you when the collection finishes.",
        viewTaskCenter: "View Task Center",
        loginToView: "Log in to View",
        status: {
          processing: "Processing",
          queued: "Queued",
          completed: "Completed",
          failed: "Failed"
        }
      },
      success: {
        title: "Task Created",
        subtitle: "We started collecting your product data. Continue to preview or track it in Task Center.",
        taskId: "Task ID",
        source: "Source URL",
        status: "Status",
        statusProcessing: "Processing",
        statusHint: "Collection is running in the background.",
        continue: "Continue Import",
        goTaskCenter: "Go to Task Center",
        loginTaskCenter: "Log in to View Tasks",
        loginHint: "Log in to unlock Task Center tracking."
      }
    },

    // General Info Settings
    generalInfo: {
      title: "General Information",
      subtitle: "Manage your account details and profile information.",
      proPlan: "Pro Plan",
      upgradePlan: "Upgrade Plan",
      uploadNew: "Upload New",
      remove: "Remove",
      photoHint: "At least 400x400 px recommended. JPG or PNG is allowed.",
      fullName: "Full Name",
      fullNamePlaceholder: "e.g. Alex Chen",
      professionalEmail: "Professional Email",
      emailPlaceholder: "name@company.com",
      companyName: "Company Name",
      companyPlaceholder: "Enter your company name",
      cancel: "Cancel",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      footer: "Platform Settings"
    },

    // Billing & Credits
    billing: {
      title: "Billing & Credits",
      subtitle: "Track credit usage, image billing rules, and your full payment history.",
      currentBalance: "Current Balance",
      credits: "Credits",
      balanceHint: "Subscription credits reset monthly; top-up credits never expire.",
      topUp: "Top Up",
      subscriptionPlan: "Subscription Plan",
      proPlanPrice: "Pro Plan · 560 credits/mo ($49)",
      nextRenewal: "Next renewal on February 24, 2026",
      managePlan: "Manage Plan",
      billingHistory: "Billing History",
      date: "Date",
      description: "Description",
      amount: "Amount",
      invoice: "Invoice",
      proSubscription: "Pro Plan Subscription (560 Credits)",
      creditTopUp: "Credit Top-up (300 Credits)",
      downloadAll: "Download All Invoices",
      footer: "All prices are in USD. Subscription credits reset monthly; top-up credits never expire.",
      footerCopyright: "Financial Operations",
      pricingTitle: "Pricing Rules",
      ruleImagePrice: "Image generation costs 2 credits per image ($0.2).",
      ruleExchange: "1 credit = $0.1. Avg. cost per image is about $0.15.",
      ruleRollover: "Subscription credits reset monthly; top-up credits never expire.",
      ruleGift: "New users get 10 credits; guests get 2 credits.",
      faqTitle: "FAQ",
      faq: {
        q1: "Why only charge for image generation?",
        a1: "Images are the primary cost driver; text and other modules are free to keep pricing simple.",
        q2: "Do subscription credits roll over?",
        a2: "No. Subscription credits reset monthly. Top-up credits are permanent.",
        q3: "What happens if generation fails?",
        a3: "Failed images are refunded automatically based on the audit record.",
        q4: "Can I top up while subscribed?",
        a4: "Yes. Top-up credits stack on top of your monthly subscription credits.",
      }
    },

    purchaseCredits: {
      title: "Purchase Credits",
      subtitle: "Choose a credit pack for image generation (2 credits per image).",
      priceHint: "1 credit = $0.1. Top-up credits never expire.",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      imagesUnit: "images",
      bestValue: "Best Value",
      saveLabel: "Save",
      featureNeverExpire: "Top-up credits never expire",
      featureBatch: "Batch generation",
      featureNoBatch: "No batch generation",
      featurePriority: "Priority support",
      paymentTitle: "Payment Method",
      purchaseButton: "Complete Purchase",
      processing: "Processing payment...",
      success: "Purchase successful! Credits added.",
      acceptedMethods: "Accepted Methods",
      visaLabel: "Visa ending in 4242",
      visaExpire: "Expires 12/26",
      wechat: "WeChat Pay",
      wechatDesc: "Fast and secure mobile payment",
      alipay: "Alipay",
      alipayDesc: "Global payment solution",
      addNew: "Add New Payment Method",
      addNewDesc: "Credit Card, WeChat Pay, or Alipay",
      security: "Secured by Stripe SSL Encryption",
      total: "Total to Pay",
      footer: "© 2026 OneClickSell Financial Operations"
    },

    subscription: {
      title: "Manage Subscription Plan",
      subtitle: "Select the plan that matches your image generation volume.",
      currentPlanLabel: "Current Plan",
      currentPlanBadge: "Current Plan",
      activeTag: "Active",
      nextBilling: "Next billing date:",
      nextBillingDate: "Feb 24, 2026",
      activeCycle: "Active Cycle",
      monthly: "Monthly",
      yearly: "Yearly",
      yearlySave: "2 months free",
      billedMonthly: "Billed monthly",
      billedYearly: "Billed yearly (10 months)",
      rolloverHint: "Subscription credits reset monthly; top-up credits never expire.",
      perMonth: "/mo",
      perYear: "/yr",
      planStarterDesc: "Ideal for individuals getting started.",
      planProDesc: "Best for growing teams.",
      planBusinessDesc: "Designed for high-volume sellers.",
      featureStarter1: "Basic batch generation",
      featureStarter2: "HD image downloads",
      featureStarter3: "Standard support (48h)",
      featurePro1: "Advanced batch generation",
      featurePro2: "HD + 4K image downloads",
      featurePro3: "Priority support (12h)",
      featurePro4: "Dedicated growth manager",
      featureBusiness1: "High-volume batch generation",
      featureBusiness2: "Brand asset kit exports",
      featureBusiness3: "Priority support + success manager",
      featureBusiness4: "Workflow automation guidance",
      downgrade: "Downgrade",
      upgrade: "Upgrade",
      activePlan: "Active Plan",
      contactSales: "Contact Sales",
      customTitle: "Need a custom plan?",
      customDesc: "Our team can tailor a plan for your volume and workflow.",
      consultSales: "Consult with Sales",
      cancel: "Cancel Subscription",
      cancelConfirm: "Are you sure you want to cancel?",
      footer: "© 2026 OneClickSell Financial Operations"
    },

    // Subscription Settings
    subscriptionSettings: {
      title: "Manage Subscription Plan",
      subtitle: "Select the best plan for your business needs.",
      currentPlanLabel: "Current Plan:",
      currentPlan: "Pro Plan",
      active: "Active",
      nextBillingLabel: "Next billing date:",
      nextBillingDate: "Nov 24, 2024",
      activeCycleLabel: "Active Cycle:",
      monthly: "Monthly",
      yearly: "Yearly",
      save20: "Save 20%",
      billedAnnually: "Billed annually for maximum savings",
      billed: "billed {cycle}",
      perMonth: "/mo",
      plans: {
        starter: {
          title: "Starter",
          desc: "Perfect for individuals and small startups."
        },
        pro: {
          title: "Pro",
          desc: "Most popular for growing teams."
        },
              },
      features: {
        starterCredits: "500 Credits per month",
        starterAgents: "Basic AI Agents access",
        starterSupport: "Email support (48h)",
        customIntegrations: "Custom integrations",
        proCredits: "2,000 Credits per month",
        proAgents: "All AI Agents access",
        proSupport: "Priority support (12h)",
        proIntegrations: "Up to 5 integrations",
              },
      buttons: {
        downgrade: "Downgrade",
        activePlan: "Active Plan",
        contactSales: "Contact Sales",
        consultSales: "Consult with Sales",
        cancelSubscription: "Cancel Subscription"
      },
      currentBadge: "Current Plan",
      custom: {
        title: "Need a custom plan?",
        desc: "Our sales experts can help you design a tailored solution that fits your specific high-volume business requirements."
      },
      alerts: {
        downgrade: "Downgrading to Starter...",
        contactSales: "Contacting sales team...",
        openChat: "Opening sales chat...",
        cancelConfirm: "Are you sure?",
        cancelRequested: "Subscription cancellation requested."
      }
    },

    // Security Settings
    securitySettings: {
      title: "Security",
      subtitle: "Manage your account protection, authentication methods, and active sessions to keep your data secure.",
      twoFactor: {
        title: "Two-Factor Authentication (2FA)",
        desc: "Secure your account with an extra layer of protection. When enabled, you'll be asked for a verification code when signing in from a new device."
      },
      password: {
        title: "Password Management",
        updated: "Last updated on October 12, 2023",
        reset: "Reset Password"
      },
      sessions: {
        title: "Active Sessions",
        subtitle: "These are the devices that have logged into your account. Revoke any sessions that you do not recognize.",
        current: "Current",
        activeNow: "Active Now",
        signOut: "Sign Out",
        chromeDevice: "Chrome on macOS",
        chromeLocation: "San Francisco, US • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "London, UK • 2 days ago"
      },
      cancel: "Cancel",
      saving: "Saving...",
      saved: "Saved",
      save: "Save Changes",
      signOutAlert: "Signing out device...",
      footer: "Security Infrastructure"
    },

    // Notifications Settings
    notificationsSettings: {
      title: "Notifications",
      subtitle: "Manage how and where you receive system updates and alerts.",
      table: {
        type: "Notification Type",
        email: "Email",
        push: "Push"
      },
      listing: {
        title: "Listing Success",
        desc: "Alert when products are published"
      },
      credit: {
        title: "Credit Warning",
        desc: "Alert when balance falls below 10%"
      },
      updates: {
        title: "Updates & Releases",
        desc: "New features and platform improvements"
      },
      mute: {
        title: "Mute all notifications",
        desc: "Temporarily silence all alerts and emails across the platform"
      },
      discard: "Discard Changes",
      saving: "Saving...",
      saved: "Preferences Saved",
      save: "Save Preferences"
    },

    // Integrations
    integrations: {
      title: "Integrations",
      subtitle: "Connect your favorite tools and marketplaces to streamline your workflow.",
      connected: "Connected",
      connect: "Connect",
      shopify: "Shopify",
      shopifyDesc: "Sync products and inventory.",
      amazon: "Amazon Seller",
      amazonDesc: "Import listings and manage orders.",
      ozon: "Ozon",
      ozonDesc: "Russian marketplace integration.",
      slack: "Slack",
      slackDesc: "Receive notifications in your channels."
    },

    // Platform Preferences
    platformPreferences: {
      title: "Platform Preferences",
      subtitle: "Customize your default marketplace settings and AI agent behavior.",
      targetMarketplace: "Default Target Marketplace",
      primaryPlatform: "Primary Platform",
      targetCountry: "Target Country",
      russia: "Russia",
      us: "United States",
      uk: "United Kingdom",
      germany: "Germany",
      aiPersona: "AI Writing Persona",
      poweredBy: "Powered by LexiCopy",
      persuasive: "Persuasive",
      professional: "Professional",
      minimalist: "Minimalist",
      seoFocused: "SEO-Focused",
      personaHint: "This style will be applied by default to all new product descriptions and ad copies.",
      brandProtection: "Automatic Brand Protection",
      brandName: "Default Brand Name",
      brandPlaceholder: "Enter brand name for watermarking...",
      autoWatermark: "Auto-apply AI watermarking",
      autoWatermarkDesc: "Protect all new generations with your brand signature",
      imagePreferences: "Image Preferences",
      deepEnhancement: "Deep Image Enhancement",
      deepEnhancementDesc: "AI upscale and clarity boost",
      autoBgRemoval: "Auto-Background Removal",
      autoBgRemovalDesc: "Clean marketplace-ready assets",
      autoSaveHint: "Changes are saved automatically to your workspace profile.",
      resetDefaults: "Reset to Defaults",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      footer: "Global Workspace"
    },

    // Assets Management
    assets: {
      nav: "Assets",
      title: "Asset Management",
      subtitle: "Manage and sync your AI-optimized product listings.",
      newOptimization: "New Optimization",
      platformAll: "Platform: All",
      statusPublished: "Status: Published",
      last30Days: "Last 30 Days",
      clearFilters: "Clear Filters",
      score: "Score",
      published: "Published",
      draft: "Draft",
      failed: "Failed",
      retry: "Retry",
      edit: "Edit",
      view: "View",
      addNewListing: "Add New Listing",
      addNewDesc: "Upload raw photos for AI enhancement.",
      assetsSelected: "Assets Selected",
      batchDownload: "Batch Download",
      batchSync: "Batch Sync",
      delete: "Delete",
      showingAssets: "Showing 1 to {count} of {total} assets",
      searchPlaceholder: "Search assets..."
    },

    // Import Preview
    importPreview: {
      nav: "Product Preview",
      breadcrumb: "Workspace",
      collectStatus: "Collection Status",
      collectQueued: "Queued",
      collecting: "Collecting",
      collectCompleted: "Completed",
      collectFailed: "Failed",
      collectingHint: "The extension is collecting data in the background.",
      collectProgressTitle: "Collection Progress",
      collectProgressHint: "We are syncing with the extension. You'll see live updates here.",
      progressLabel: "Progress",
      stepQueued: "Waiting for Extension",
      stepAssigned: "Extension Accepted",
      stepCollecting: "Collecting Data",
      stepUploading: "Uploading Data",
      stepCompleted: "Collection Complete",
      currentStage: "Current Stage",
      stage: {
        pending: "Waiting for extension",
        assigned: "Task assigned",
        preparing: "Preparing page",
        scrolling: "Scrolling page",
        collecting: "Collecting content",
        saving: "Saving data",
        uploading: "Uploading assets",
        cached: "Loading cached result",
        completed: "Completed"
      },
      authRequired: "Please log in to view collection progress.",
      pollFailed: "Failed to fetch collection progress.",
      previewFailed: "Failed to load collected data.",
      attributeList: "Additional Attributes",
      detailHighlights: "Detail Highlights",
      noHighlights: "No highlight details detected yet.",
      skuStructure: "SKU Structure",
      skuStock: "Stock",
      skuVariant: "Variant",
      noSkuData: "No SKU structure data detected yet.",
      productDetails: "Product Details",
      detailSummary: "Detail Summary",
      noDetailSummary: "No detail summary available yet.",
      noImages: "No images yet",
      dataOverview: "Data Overview",
      overviewImages: "Images",
      overviewSkus: "SKUs",
      overviewAttributes: "Attributes",
      overviewSections: "Raw Sections",
      overviewRawText: "Raw Text Length",
      characters: "chars",
      noOverview: "No overview data yet.",
      flags: "Collection Flags",
      noFlags: "No flags detected yet.",
      flagEnabled: "Yes",
      flagDisabled: "No",
      dimensionsInfo: "Dimensions & Weight",
      noDimensions: "No dimension info available yet.",
      imageTags: "Image Tags",
      noImageTags: "No image tag data yet.",
      rawText: "Raw Text",
      noRawText: "No raw text available yet.",
      rawSections: "Raw Sections",
      validationWarning: "Data Validation Issues",
      issuesDetected: "issues detected that may affect AI optimization",
      manualReview: "Manual Review Required",
      allResolved: "All Issues Resolved",
      source: "Source",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price (1688)",
      priceRange: "Price Range",
      invalid: "Invalid",
      aiSuggest: "AI Suggest",
      marketAvg: "Market Avg. Price",
      suggestedAiPrice: "Suggested AI Price",
      profitMargin: "Profit Margin",
      marginDesc: "Estimated 72.4% net margin after typical shipping.",
      aiStrategy: "AI Optimization Strategy",
      seoEnhancements: "SEO Enhancements",
      seoDesc: "Will add \"orthopedic support\" and \"breathable mesh\" keywords to boost search visibility by 35%.",
      imageRefinement: "Image Refinement",
      imageDesc: "AI will remove 1688 watermarks and generate lifestyle backgrounds for main photos.",
      marketPositioning: "Market Positioning",
      positionDesc: "Targeting \"Affordable Premium\" segment with focus on urban durability.",
      skuAttributes: "SKU Attributes",
      colorVariants: "Color Variants",
      sizeAvailability: "Size Availability",
      currentStock: "Current Stock",
      shippingEst: "Shipping Est.",
      shippingLocal: "3-5 Days (Local)",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "MISSING",
      aiAutofill: "AI Autofill Missing Data",
      dataFilled: "Data Filled by AI",
      viewMetadata: "View All Raw Metadata",
      lowRes: "Low Res",
      dataRequired: "DATA VALIDATION REQUIRED",
      readyOptimize: "READY FOR OPTIMIZATION",
      agentsPaused: "Optimization agents paused",
      aiFillAll: "AI Fill All Missing Data",
      saveDraft: "Save Draft",
      saved: "Saved",
      startOptimization: "Start AI Optimization",
      optimized: "Optimized",
      fixIssues: "Fix issues to improve AI results",
      exportToStore: "Export to Store",
      exporting: "Exporting...",
      exported: "Exported!",
      exportSuccess: "Product successfully exported to store!"
    },

    // Review (Updated for AI Agent Canvas)
    review: {
      nav: "AI Data Review",
      validating: "AI is suggesting values based on product category.",
      validated: "AI Optimization Successful. All fields validated.",
      pending: "Manual Review Required",
      completed: "Review Completed",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price",
      invalid: "Invalid",
      marketAvg: "Market Avg. Price",
      suggestedPrice: "Suggested AI Price",
      aiGenerated: "AI Generated",
      marginAnalysis: "Profit Margin Analysis",
      marginDesc: "Estimated 72.4% net margin after localized shipping.",
      strategy: "AI Optimization Strategy",
      seoEnhance: "SEO Enhancements",
      seoDesc: "Will add optimized keywords to boost visibility.",
      imgRefine: "Image Refinement",
      imgDesc: "AI will remove watermarks and generate lifestyle backgrounds.",
      marketPos: "Market Positioning",
      marketPosDesc: "Targeting 'Affordable Premium' segment for durability.",
      skuAttrs: "SKU Attributes",
      originalData: "Original Data",
      colorVariants: "Color Variants",
      sizeAvail: "Size Availability",
      stock: "Current Stock",
      shipping: "Shipping Est.",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "Missing",
      confirmBtn: "Confirmed All AI Suggestions",
      autofillBtn: "AI Autofill Missing Data",
      saveDraft: "Save Draft",
      applyAi: "Apply AI Changes",
      startAi: "Start AI Optimization",
      exportBtn: "Export to Store"
    }
  },
  zh: {
    ".": ".",
    appName: "一键大卖",
    trustAppName: "一键大卖 Trust",

    // 导航
    nav: {
      features: "功能",
      pricing: "定价",
      faq: "常见问题",
      login: "登录",
      startFree: "免费开始"
    },

    appNav: {
      more: "更多",
      loginHint: "登录后可使用该功能。",
      unlockHint: "完成导入后解锁。"
    },

    admin: {
      title: "管理后台",
      subtitle: "运营与配置中心",
      backToWorkspace: "返回工作台",
      login: {
        title: "管理员登录",
        subtitle: "请输入管理员账号密码",
        checking: "正在验证登录状态...",
        username: "管理员账号",
        usernamePlaceholder: "请输入管理员账号",
        password: "管理员密码",
        passwordPlaceholder: "请输入管理员密码",
        action: "登录后台",
        loggingIn: "登录中...",
        required: "请输入账号和密码",
        failed: "登录失败",
        denied: "无管理员权限",
        logout: "退出登录"
      },
      nav: {
        overview: "概览",
        aiChannels: "AI 渠道",
        aiMonitor: "渠道监控",
        billing: "计费与积分",
        consumption: "消费明细",
        users: "用户管理",
        logs: "操作日志",
        settings: "系统设置"
      },
      access: {
        checking: "正在验证管理员权限...",
        loginTitle: "请先登录",
        loginDesc: "管理后台需要登录后访问。",
        loginAction: "立即登录",
        deniedTitle: "无权限访问",
        deniedDesc: "当前账号不是管理员。"
      },
      overview: {
        title: "数据概览",
        desc: "核心指标与最近趋势",
        loading: "正在加载概览数据...",
        error: "加载概览数据失败。",
        empty: "暂无数据。",
        userTrend: "最近 7 天新增用户",
        revenueTrend: "最近 7 天收入",
        cards: {
          totalUsers: "累计用户",
          activeToday: "今日活跃",
          newToday: "今日新增",
          totalRevenue: "累计收入",
          revenueMonth: "本月收入",
          totalConsumption: "累计消费"
        }
      },
      aiChannels: {
        title: "AI 渠道管理",
        desc: "管理平台 AI 渠道与路由策略",
        error: "加载渠道失败。",
        validationName: "请输入渠道名称。",
        validationKey: "请输入 API Key。",
        updated: "渠道已更新。",
        created: "渠道已创建。",
        tested: "测试已完成。",
        deleteConfirm: "确认删除渠道 {name}？",
        refresh: "刷新",
        updatedAt: "更新时间 {time}",
        edit: "编辑渠道",
        create: "新增渠道",
        cancel: "取消编辑",
        formHint: "编辑时留空 API Key 可保持原值。",
        summary: {
          total: "渠道总数",
          enabled: "启用渠道",
          degraded: "异常渠道",
          requests: "24小时请求",
          failureRate: "24小时失败率"
        },
        fields: {
          name: "渠道名称",
          apiKey: "API Key",
          baseUrl: "Base URL",
          tier: "等级",
          priority: "优先级",
          maxFail: "最大失败次数",
          recovery: "恢复时间（秒）",
          description: "描述",
          enabled: "启用",
          isOfficial: "官方渠道"
        },
        placeholders: {
          name: "例如：Gemini-Primary",
          apiKey: "粘贴 API Key",
          apiKeyEdit: "留空则保持不变",
          baseUrl: "https://api.example.com",
          description: "用途说明"
        },
        save: "保存",
        saving: "保存中...",
        listTitle: "渠道列表",
        loading: "正在加载渠道...",
        empty: "暂无渠道配置。",
        statusUnknown: "未知",
        table: {
          name: "渠道",
          status: "状态",
          priority: "优先级",
          baseUrl: "Base URL",
          requests: "24小时请求",
          failures: "24小时失败",
          successRate: "成功率",
          avgLatency: "平均耗时",
          actions: "操作"
        },
        actions: {
          edit: "编辑",
          disable: "停用",
          enable: "启用",
          test: "测试",
          delete: "删除"
        }
      },
      aiMonitor: {
        title: "渠道运行监控",
        desc: "查看渠道实时状态与健康度",
        loading: "正在加载运行状态...",
        error: "加载渠道状态失败。",
        refresh: "刷新",
        autoRefresh: "自动刷新",
        updatedAt: "更新时间 {time}",
        current: "当前渠道",
        total: "渠道数量",
        healthy: "健康渠道",
        requests: "24小时请求",
        failureRate: "失败率",
        avgLatency: "平均耗时",
        trend: "调用趋势",
        last24h: "最近 24 小时",
        noTrend: "暂无趋势数据",
        errors: "近期错误",
        errorsDesc: "最近 24 小时失败记录",
        noErrors: "暂无错误",
        none: "暂无",
        empty: "暂无运行数据。",
        statusUnknown: "未知",
        yes: "是",
        no: "否",
        table: {
          name: "渠道",
          status: "状态",
          priority: "优先级",
          enabled: "启用",
          available: "可用",
          successRate: "成功率",
          failCount: "失败次数",
          avgLatency: "平均耗时"
        }
      },
      billing: {
        title: "积分与计费",
        desc: "调整用户积分与手动充值",
        error: "加载积分用户失败。",
        actionTitle: "积分操作",
        validationUser: "请填写用户 ID。",
        defaultReason: "管理员调整",
        rechargeReason: "管理员充值",
        actionSuccess: "操作完成。",
        submitting: "提交中...",
        submit: "提交",
        listTitle: "用户积分列表",
        search: "查询",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        fields: {
          userId: "用户 ID",
          actionType: "操作类型",
          reason: "原因",
          credits: "积分",
          amount: "金额",
          bonus: "赠送"
        },
        placeholders: {
          userId: "从列表选择或粘贴 ID",
          reason: "可选原因",
          search: "搜索用户"
        },
        actions: {
          adjust: "调整积分",
          recharge: "充值",
          select: "选择"
        },
        table: {
          user: "用户",
          plan: "套餐",
          credits: "积分",
          trial: "试用",
          actions: "操作"
        }
      },
      consumption: {
        title: "消费明细",
        desc: "查看积分消耗与扣费记录",
        loading: "正在加载消费明细...",
        error: "加载消费明细失败。",
        empty: "暂无记录。",
        search: "查询",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          userId: "按用户 ID 筛选"
        },
        table: {
          user: "用户",
          product: "商品",
          source: "来源",
          unitPrice: "单价",
          createdAt: "时间"
        }
      },
      users: {
        title: "用户管理",
        desc: "管理用户状态、套餐与管理员权限",
        error: "加载用户失败。",
        filters: "筛选条件",
        search: "搜索",
        filtersAllStatus: "全部状态",
        filtersAllAdmin: "全部用户",
        filtersAdmin: "仅管理员",
        filtersNonAdmin: "非管理员",
        editorTitle: "编辑用户",
        detailTitle: "用户详情",
        closeEditor: "关闭",
        adminToggle: "管理员权限",
        saved: "已保存",
        saving: "保存中...",
        save: "保存修改",
        listTitle: "用户列表",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        fields: {
          status: "状态",
          plan: "套餐",
          admin: "管理员",
          notes: "备注"
        },
        detailLabels: {
          userId: "用户 ID",
          phone: "手机号",
          email: "邮箱",
          plan: "套餐",
          status: "状态",
          credits: "积分",
          trial: "试用",
          createdAt: "创建时间",
          lastLogin: "最近登录"
        },
        placeholders: {
          search: "按用户名/手机号/邮箱搜索",
          notes: "管理员备注"
        },
        status: {
          active: "正常",
          inactive: "未激活",
          banned: "封禁"
        },
        plan: {
          free: "免费版",
          earlyBird: "早鸟版",
          standard: "标准版"
        },
        table: {
          user: "用户",
          plan: "套餐",
          status: "状态",
          credits: "积分",
          actions: "操作"
        },
        actions: {
          manage: "管理"
        }
      },
      logs: {
        title: "操作日志",
        desc: "管理员操作审计",
        loading: "正在加载日志...",
        error: "加载日志失败。",
        empty: "暂无日志。",
        search: "查询",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          action: "按操作筛选",
          admin: "按管理员 ID 筛选"
        },
        table: {
          action: "操作",
          target: "目标",
          admin: "管理员",
          details: "详情",
          ip: "IP",
          createdAt: "时间"
        }
      },
      settings: {
        title: "系统设置",
        desc: "调整平台运行参数",
        error: "加载设置失败。",
        loading: "正在加载设置...",
        empty: "暂无设置项。",
        saved: "设置已保存。",
        save: "保存",
        boolean: "启用",
        updatedAt: "更新时间 {time}",
        groups: {
          business: "业务",
          feature: "功能",
          other: "其他"
        }
      }
    },

    // 首页横幅
    hero: {
      badge: "AI 驱动的电商平台",
      title: "一键转化商品",
      titleHighlight: "上架全球市场",
      subtitle: "粘贴链接，AI 自动优化，一键发布到各大电商平台。",
      placeholder: "粘贴 1688 或速卖通链接...",
      button: "开始 AI 优化"
    },

    // 功能展示 1
    feature1: {
      title: "见证 AI 魔法",
      subtitle: "看我们如何将供应商原始数据转化为专业的电商 Listing。",
      tab1: "智能手表",
      tab2: "耳机",
      tab3: "运动鞋",
      imgBtn1: "AI 优化后",
      imgBtn2: "原图",
      imgBtn3: "对比",
      aiBadge: "AI 优化",
      marketingTitle: "营销素材",
      viewAll: "查看全部",
      listingTitleTag: "商品标题",
      translated: "已翻译",
      bulletTitle: "核心卖点",
      seoTitle: "SEO 关键词",
      pushBtn: "发布到平台",
      syncText: "已同步到 Ozon",
      // 手表产品
      prodTitle_watch: "高端智能手表 Pro Max",
      bullet1Title_watch: "健康监测",
      bullet1Desc_watch: "24小时心率、血氧、睡眠追踪",
      bullet2Title_watch: "超长续航",
      bullet2Desc_watch: "单次充电可用 14 天",
      key1_watch: "智能手表",
      key2_watch: "健身追踪器",
      key3_watch: "健康监测",
      hotspot1: "AMOLED 屏幕",
      hotspot2: "钛金属表壳",
      // 耳机产品
      prodTitle_headphones: "无线降噪耳机 Studio",
      bullet1Title_headphones: "主动降噪",
      bullet1Desc_headphones: "隔绝 99% 环境噪音",
      bullet2Title_headphones: "Hi-Res 音质",
      bullet2Desc_headphones: "40mm 驱动单元，支持 LDAC",
      key1_headphones: "无线耳机",
      key2_headphones: "降噪耳机",
      key3_headphones: "蓝牙耳机",
      hotspot_headphones_1: "记忆海绵耳垫",
      hotspot_headphones_2: "触控操作",
      // 运动鞋产品
      prodTitle_sneakers: "超轻跑步鞋 X9",
      bullet1Title_sneakers: "云端缓震",
      bullet1Desc_sneakers: "革命性泡棉科技",
      bullet2Title_sneakers: "透气网面",
      bullet2Desc_sneakers: "360° 通风系统",
      key1_sneakers: "跑步鞋",
      key2_sneakers: "运动鞋",
      key3_sneakers: "休闲运动鞋",
      hotspot_sneakers_1: "碳板支撑",
      hotspot_sneakers_2: "抓地大底"
    },

    // 功能展示 2 - 使用流程
    feature2: {
      title: "使用流程",
      subtitle: "三步走向全球市场。",
      card1Title: "导入分析",
      card1Desc: "粘贴任意供应商链接，AI 自动提取商品数据、图片和规格。",
      card2Title: "AI 优化",
      card2Desc: "生成 SEO 标题、翻译描述、优化图片、创建 A+ 内容，秒级完成。"
    },

    // 功能展示 3 - 安全
    feature3: {
      badge: "企业级安全",
      title: "保护您的品牌资产",
      subtitle: "军事级别的图片和 Listing 保护。",
      verified: "已验证",
      watermark: "神经水印",
      hash: "SHA-256 哈希",
      cert: "已认证",
      card1Title: "隐形水印",
      card1Desc: "AI 嵌入的标记，经过压缩和裁剪仍然有效。",
      card2Title: "法律证据包",
      card2Desc: "自动生成 DMCA 删除文件和区块链证明。",
      card3Title: "即时检测",
      card3Desc: "当您的图片出现在未授权的 Listing 时收到提醒。",
      downloadBtn: "下载白皮书",
      downloadSub: "安全架构概述",
      learnMore: "了解更多保护方案"
    },

    // 定价
    pricing: {
      title: "简单透明的定价",
      subtitle: "只对图片生成收费，每张 2 积分。",
      monthly: "月付",
      yearly: "年付",
      save: "送 2 个月",
      bestForGuests: "游客首选",
      onDemand: "按需付费",
      credits: "积分",
      creditsLabel: "积分",
      imagesLabel: "张图片",
      perMonth: "/月",
      perYear: "/年",
      packBasic: "基础包",
      packGrowth: "成长包",
      packScale: "扩容包",
      bestValue: "最划算",
      featExpires: "充值积分永久有效",
      featAi: "AI 图片生成",
      featPsd: "高清素材下载",
      featPriority: "优先支持",
      purchase: "立即购买",
      planStarter: "入门版",
      planPro: "专业版",
      planBusiness: "商务版",
      featStore: "每月赠送积分",
      featBatch: "批量图片生成",
      featSupport: "优先支持",
      featBatchStarter: "基础批量生成",
      featBatchPro: "高级批量生成",
      featBatchBusiness: "高并发批量生成",
      featSupportStarter: "标准支持（48 小时）",
      featSupportPro: "优先支持（12 小时）",
      featSupportBusiness: "优先支持 + 成功经理",
      featExportsStarter: "高清素材下载",
      featExportsPro: "高清 + 4K 素材下载",
      featExportsBusiness: "品牌素材包导出"
    },

    // 常见问题
    faq: {
      title: "常见问题",
      subtitle: "关于平台的一切你需要知道的。",
      contact: "联系客服",
      q1: "AI 优化会改坏我的商品信息吗？",
      a1: "不会。系统以原始数据为基准生成优化方案，所有字段在发布前可预览、可编辑，确保不会改变关键属性。",
      q2: "需要安装插件或绑定店铺吗？",
      a2: "无需安装插件或绑定店铺即可体验，粘贴链接即可开始。仅在发布或同步到平台时才需要授权。",
      q3: "我的数据和图片安全吗？",
      a3: "我们采用企业级加密与权限隔离，图片生成自带隐形水印与取证信息，防止被盗用。",
      q4: "试用与收费如何计算？",
      a4: "新用户可免费体验 1 次任务，体验仅可生成 1 张主图；后续按订阅或积分计费，透明无隐藏费用。"
    },

    // CTA
    cta: {
      title: "准备好扩展",
      titleBreak: "您的电商业务了吗？",
      subtitle: "加入数千名已在使用 AI 征战全球市场的卖家。",
      button: "免费开始"
    },

    // 页脚
    footer: {
      privacy: "隐私政策",
      terms: "服务条款",
      support: "支持",
      rights: "2024 一键大卖 版权所有"
    },

    // Trust 页面
    trust: {
      forensicData: "取证元数据",
      badge: "企业级安全",
      title: "保护您的",
      titleHighlight: "数字资产",
      subtitle: "军事级别的产品图片和品牌保护。",
      dmcaBadge: "DMCA 保护",
      forensicBadge: "取证就绪",
      encryptedBadge: "256位加密",
      nav: {
        technology: "技术方案",
        legal: "法律框架",
        whitepaper: "资源"
      },
      tech1Title: "神经水印",
      tech1Desc: "隐形标记，经过任何转换仍然有效。",
      tech2Title: "区块链时间戳",
      tech2Desc: "分布式账本上不可篡改的创作证明。",
      tech3Title: "AI 检测",
      tech3Desc: "全平台实时监控。",
      compareTitle: "看看区别",
      compareSub: "移动滑块查看隐藏水印。",
      rawOutput: "原始输出",
      status: "状态",
      ctaBtn: "开始保护",
      protectedLayer: "防护层",
      frameworkTitle: "法律框架",
      frameworkSub: "合规文档与可验证证据链。",
      licenseTitle: "授权协议",
      licenseDesc: "自动生成授权文件并提供不可篡改证明。",
      dmcaPackageTitle: "DMCA 取证包",
      dmcaPackageDesc: "一键生成平台下架所需证据材料。",
      complianceTitle: "合规就绪",
      complianceDesc: "满足平台执法要求的结构化证据。",
      platformLabel: "平台",
      priority: "优先级",
      refLabel: "参考编号",
      evidenceLabel: "证据",
      evidence1: "原始商品截图",
      evidence2: "水印验证哈希",
      generatorTitle: "自动 DMCA 生成器",
      generatorSub: "几分钟内生成完整执法文件。",
      generatorNote: "兼容 Amazon、Shopify、Etsy 等 30+ 平台。",
      letterAutomation: "自动生成 DMCA 通知",
      letterBody1: "本通知确认版权所有及侵权证据。",
      letterBody2: "所有元数据与时间戳已链上验证。",
      letterBody3: "相关证据已随案件材料附上。",
      letterBody4: "请立即移除侵权内容。",
      letterNotice: "本通知已准备好提交。",
      letterSign: "授权代表",
      ctaTitle: "准备好保护品牌了吗？",
      ctaTitleBreak: "马上启动执法",
      ctaSub: "5 分钟内完成合规保护部署。",
      downloadBtn: "下载证据包",
      verifiedHash: "已验证哈希",
      urlLabel: "侵权链接",
      selectPlatform: "选择平台",
      toLabel: "致",
      rights: "版权所有"
    },

    welcome: "欢迎回来",
    loginSubtitle: "请输入您的详细信息以登录您的专业帐户。",
    smsMethod: "短信验证码",
    passMethod: "密码",
    phoneLabel: "手机号码",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "手机号码",
    phoneRequired: "请先填写手机号。",
    phoneInvalid: "手机号格式不正确。",
    sendCode: "发送验证码",
    sendCodeFailed: "验证码发送失败，请稍后重试。",
    debugCodeHint: "开发环境验证码：{code}",
    verifyLabel: "验证码",
    resendIn: "{time}秒后重发",
    resendNow: "重新发送",
    invalidCode: "验证码无效。请尝试 123456。",  // pragma: allowlist secret
    loginFailed: "登录失败，请稍后重试。",
    passwordLabel: "密码",
    forgotPass: "忘记密码？",
    passPlaceholder: "输入您的密码",
    passwordRequired: "请输入密码。",
    passwordTooShort: "密码至少 8 位。",
    signIn: "登录",
    newToApp: "新用户？",
    signUp: "注册",
    createAccount: "创建您的账户",
    signupSubtitle: "加入 5,000+ 优化列表的卖家。",
    codeVerified: "验证码已验证",
    setPassPlaceholder: "设置您的密码",
    agree: "我同意",
    userAgreement: "用户协议",
    and: "和",
    privacyPolicy: "隐私政策",
    getStarted: "免费开始",
    haveAccount: "已有账户？",
    login: "登录",
    resetTitle: "重置密码",
    resetSubtitle: "输入您的手机号码以接收验证码。",
    sendReset: "发送重置码",
    resetStep1: "验证手机号",
    resetStep2: "输入验证码",
    resetStep3: "设置新密码",
    resetCodeSent: "验证码已发送，请注意查收。",
    resetCodeHint: "请输入 6 位验证码",
    resetVerifyAction: "验证并继续",
    resetBackStep: "返回上一步",
    resetMismatch: "两次密码不一致。",
    resetRequirement: "至少 8 位，包含字母和数字。",
    resetSuccess: "密码已更新。",
    resetSuccessHint: "正在返回登录...",
    resetContextMissing: "重置会话已失效，请重新获取验证码。",
    resetFailed: "密码重置失败，请稍后重试。",
    resetStrengthLabel: "强度：{level}",
    resetStrengthWeak: "弱",
    resetStrengthMedium: "中",
    resetStrengthStrong: "强",
    backLogin: "返回登录",
    phoneVerified: "手机已验证",
    setNewPass: "设置新密码",
    newPassSubtitle: "请创建一个强密码以保护您的账户。",
    newPassLabel: "新密码",
    newPassPlaceholder: "创建新密码",
    strength: "强度：强",
    confirmPassLabel: "确认新密码",
    confirmPassPlaceholder: "重复新密码",
    updatePass: "更新密码",
    backSignIn: "返回登录",
    captchaLabel: "人机验证",
    captchaHint: "请先完成验证以继续。",
    captchaLoading: "验证加载中...",
    captchaRequired: "请先完成人机验证。",
    captchaUnavailable: "人机验证暂不可用，请稍后再试。",
    captchaLoadFailed: "验证加载失败，请刷新重试。",
    captchaError: "验证失败，请重试。",

    profile: {
      proPlan: "专业版",
      freePlan: "免费版",
      guest: "游客",
      coreNav: "核心导航",
      workspace: "工作区",
      assetMgmt: "资产管理",
      copyright: "版权资产",
      pricing: "价格策略",
      storeAuth: "店铺授权",
      marketIntel: "市场情报",
      beta: "Beta",
      account: "账户",
      settings: "账户设置",
      billing: "账单与积分",
      help: "帮助中心",
      signOut: "退出登录"
    },

    settings: {
      general: "基本信息",
      security: "安全",
      preferences: "平台偏好",
      billing: "账单与积分",
      notifications: "通知",
      integrations: "集成",
      storeAuth: "店铺授权"
    },

    // 店铺绑定
    storeBind: {
      title: "店铺授权管理",
      subtitle: "关联您的电商平台账号，开启 AI 智能发布能力。",
      guideTitle: "配置指南",
      step1Title: "登录 Ozon 后台",
      step1Desc: "前往 Ozon 卖家中心完成身份认证，开始接入流程。",
      step1Btn: "前往 Ozon 卖家中心",
      step2Title: "获取 API 凭证",
      step2Desc: "在顶部菜单中依次进入「设置」→「API 密钥」。",
      step2Btn: "打开 API 设置",
      step3Title: "生成管理员密钥",
      step3Desc: "选择「Admin」密钥类型并生成，复制 Client ID 和 API Key 填入下方表单。",

      // 店铺切换器
      activeSwitcher: "当前激活店铺",
      manageAll: "管理全部店铺",
      currentlyActive: "当前激活",
      synced: "已同步",
      connected: "已连接",
      uploads7d: "近7日上架",
      connectNew: "添加新店铺",

      // 平台选择
      fullApiIntegration: "完整 API 集成",
      readyConnect: "可立即接入",
      comingSoon: "即将支持",
      active: "已激活",

      // 连接表单
      connectOzon: "连接 Ozon 店铺",
      statusLabel: "状态",
      authorized: "已授权，准备就绪",
      noStoresLinked: "暂无已关联店铺",
      howToGetKey: "如何获取 API 密钥？",
      storeName: "店铺名称",
      storeNamePlaceholder: "例如：我的 Ozon 店铺",
      clientId: "客户端 ID",
      clientIdPlaceholder: "输入 Client ID",
      apiKey: "API 密钥",
      addStore: "添加 Ozon 店铺",
      testConnection: "测试连接",
      testing: "连接测试中...",

      // 同步状态
      syncingData: "正在同步店铺数据...",
      syncingDesc: "正在从 Ozon 导入类目、物流方式和仓储信息，请稍候片刻。",
      syncComplete: "同步完成",
      syncCompleteDesc: "类目及仓储数据已成功同步，您的店铺已准备就绪，可开始 AI 智能上架。",
      success: "成功",

      // 成功状态
      startListing: "开始智能上架",
      connectionSuccess: "连接成功",

      // 安全说明
      securityTitle: "API 连接安全保障",
      securityEncryption: "您的凭证采用 AES-256 军事级加密存储。",
      securityNoPassword: "一键大卖绝不会索取或存储您的平台登录密码。",

      // 表格
      authorizedStores: "已授权店铺",
      platform: "平台",
      actions: "操作",
      syncSettings: "同步设置",
      items: "件商品",

      // 底部
      storageSystem: "存储体系：本地化 AES 加密",
      securityGuaranteed: "安全保障",
      encryptedBridge: "加密 API 通道",
      noPasswordStorage: "零密码存储",
      cancel: "取消",
      saveAuth: "保存授权",

      // 提示
      toastTitle: "Ozon 店铺就绪",
      toastDesc: "类目及仓储数据已成功同步。"
    },

    capability: {
      title: "能力设置",
      subtitle: "配置平台特定的 AI 编排。直接集成状态因市场而异。",
      targetPlatform: "目标平台选择",
      selectOzon: "选择 Ozon 以获得完全自动化",
      supported: "完全支持",
      draftOnly: "仅草稿",
      listingPrep: "列表准备",
      systemNotice: "系统通知",
      systemNoticeBody: "完全自动化（直接上传）目前仅限于 Ozon。其他平台支持 AI 内容生成（图片 + 文案 + 基本规格）并手动上传。",
      platformActive: "Ozon 已启用",
      platformOzonDesc: "面向俄罗斯市场与本地履约优化",
      platformAmazonDesc: "Amazon 正在支持中，当前仅支持列表预处理。",
      comingSoon: "即将上线",
      targetMarket: "目标市场",
      russiaDefault: "俄罗斯（Ozon 默认）",
      outputLanguage: "输出语言",
      russianDefault: "俄语（标准西里尔文）",
      aiStrategy: "AI 内容策略",
      stratConversion: "转化优先",
      stratProfessional: "专业",
      stratMinimalist: "极简",
      stratSeo: "SEO 优先",
      strategyTier: "策略档位",
      strategyEconomy: "成本优先",
      strategyEconomyDesc: "成本更低、速度更快，保持基础优化。",
      strategyBalanced: "均衡推荐",
      strategyBalancedDesc: "成本、速度与质量的平衡方案。",
      strategyPremium: "精品质量",
      strategyPremiumDesc: "更深度优化与更高质量输出。",
      brandGuardTitle: "品牌与合规保护",
      brandGuardDesc: "为内容与图片启用品牌保护和合规保障。",
      brandGuardHint: "平台侧将继续提供高级合规防护。",
      summaryTitle: "执行摘要",
      summaryHint: "本次将执行",
      summaryImages: "生成 {count} 张图片（主图 + 场景 + 卖点）。",
      summarySeo: "SEO 深度：{level}。",
      summaryRich: "富文本主题：{theme}。",
      summaryBrandOn: "品牌保护已启用。",
      summaryBrandOff: "品牌保护已关闭。",
      summaryDegradedAmazon: "Amazon 暂不支持富文本，已降级为列表预处理。",
      saveDefault: "保存为默认",
      useDefault: "恢复默认",
      saving: "保存中...",
      saved: "已保存",
      aiContentGen: "AI 内容生成",
      aiContentDesc: "图片、标题和本地化规格",
      active: "活跃",
      directPublish: "一键直发",
      directPublishDesc: "跳过手动 CSV 导出/导入",
      ozonExclusive: "OZON 专属",
      launchBtn: "启动 AI 编排",
      status: "系统状态：准备好 Ozon 高速同步",
      secure: "安全协议",
      neural: "神经引擎 v4.2",
      apiSync: "API 同步活跃"
    },

    aiConfig: {
      badge: "AI 配置",
      pageTitle: "AI 优化设置",
      pageSubtitle: "在启动优化任务前，确认并微调 AI 参数。",
      statusHint: "AI 建议已准备好复核",
      targetPlatform: "目标平台",
      platformActive: "Ozon 已启用",
      platformOzonDesc: "面向独联体市场与本地物流优化",
      platformAmazonDesc: "全球市场优化流程",
      platformShopifyDesc: "Shopify 店铺上架准备",
      platformAliDesc: "跨境上架准备",
      comingSoon: "即将上线",
      targetLanguage: "目标语言",
      languageRu: "俄语",
      languageEn: "英语",
      contentStyle: "内容风格",
      styleProfessional: "专业",
      styleCreative: "创意",
      styleConcise: "简洁",
      styleCustom: "自定义",
      stylePrompt: "风格提示逻辑",
      currentStyle: "当前风格：",
      customActive: "自定义逻辑生效",
      promptProfessional: "提示词：突出技术规格、耐用性与商务语气。",
      promptCreative: "提示词：强调情绪价值、故事化表达与生活方式收益。",
      promptConcise: "提示词：用简洁有力的句子聚焦核心卖点。",
      promptCustom: "已加载自定义提示词，请在高级设置中精细调整。",
      customPrompt: "自定义提示词编辑器",
      customPromptPlaceholder: "在此输入自定义提示词...",
      customPromptHint: "可用变量：{product_name}、{category}、{specs}",
      customHint: "前往高级编辑器完善自定义提示词",
      imageGeneration: "开启 AI 图片生成",
      imageGenerationDesc: "去除水印并生成生活方式场景图。",
      imageCredits: "图片生成将消耗图片积分。",
      trialImageBadge: "体验仅生成 1 张主图",
      trialImageHint: "体验模式仅生成 1 张主图，升级后解锁完整图片组。",
      trialUpgradeCta: "查看套餐",
      seoLevel: "SEO 优化级别",
      seoStandard: "标准",
      seoStandardDesc: "基础关键词注入与元标签优化",
      seoDeep: "深度分析",
      seoDeepDesc: "语义聚类、竞品分析与长尾关键词整合",
      recommended: "推荐",
      launch: "启动 AI 优化",
      modalTitle: "AI 优化配置",
      modalSubtitle: "启动 AI 任务前设置最终偏好。",
      useDefault: "使用默认配置",
      advanced: "高级设置",
      cancel: "取消",
      saveDraft: "保存草稿",
      saving: "保存中...",
      saved: "已保存",
      footerHint: "AI 将按照当前策略与积分消耗执行任务。"
    },

    ai: {
      workspaceTitle: "活跃列表优化",
      syncProgress: "同步进度",
      sourceRef: "来源参考",
      rawMedia: "原始媒体资产",
      brandProt: "品牌保护",
      agentOrch: "代理编排",
      neuralCenter: "神经洞察中心",
      sourceAnalysis: "来源分析与结构",
      byImageAnalyzer: "通过图像分析器",
      dataStructurer: "数据结构化工具",
      rawImages: "原始供应商图片",
      scanned: "已扫描",
      unstructured: "非结构化文本 → 结构化属性",
      processed: "已处理",
      seoArch: "SEO 关键词图谱",
      bySeo: "通过 SEO 架构师",
      optTitle: "优化标题",
      score: "评分",
      bullets: "要点",
      keywordStrat: "关键词策略",
      core: "核心",
      hot: "热门",
      longTail: "长尾",
      visualPipeline: "视觉资产管道",
      byImgSel: "通过图像选择器",
      visualPlanner: "视觉规划师",
      refSel: "参考选择",
      sourcesLocked: "来源已锁定",
      visualPlan: "视觉计划（生成提示）",
      readyArtist: "艺术家准备就绪",
      optGallery: "Listing 文案",
      byArtist: "通过视觉艺术家",
      marketInt: "平台适配",
      byRichBuilder: "通过丰富内容构建器",
      contextMaster: "上下文大师",
      aplusPreview: "富文本预览",
      fieldMap: "平台字段映射",
      motionProd: "导出交付",
      byVideoCreator: "通过视频创作者",
      rendering: "VEO 3.1 渲染引擎",
      scriptGen: "脚本已生成",
      analyzer: "商品理解",
      structurer: "商品理解",
      architect: "SEO 关键词代理",
      planner: "视觉策略",
      selector: "视觉策略",
      richContent: "平台适配",
      videoCreator: "导出代理",
      visualArtist: "Listing 文案",
      complete: "完成",
      thinking: "思考中",
      queued: "排队中",
      waiting: "等待中",
      idle: "空闲",
      active: "活跃（渲染中）",
      enableVideo: "启用视频生成",
      reviewChanges: "审查更改",
      oneClick: "一键发布",
      neuralActive: "神经洞察中心活跃。管道针对亚马逊美国市场。"
    },

    studio: {
      control: {
        title: "生成与调试控制",
        collection: "Collection:",
        collectionEmpty: "未选择",
        marketStatus: "市场状态",
        skipImages: "跳过图片",
        start: "开始生成",
        starting: "启动中...",
        stageDebug: "单阶段调试",
        runStage: "执行单阶段",
        executing: "执行中..."
      },
      errors: {
        missingCollection: "缺少 collectionId，无法加载 Studio 数据。",
        previewFailed: "预览数据加载失败。",
        stageStatusFailed: "阶段状态加载失败。",
        generateFailed: "生成启动失败。",
        stageExecuteFailed: "单阶段执行失败。",
        agentLogFailed: "Agent 日志加载失败。",
        previewRequestFailed: "preview 请求失败（状态 {status}）。",
        imagePlanRequestFailed: "image-plans 请求失败（状态 {status}）。",
        stageStatusRequestFailed: "stage-status 请求失败（状态 {status}）。",
        generateRequestFailed: "generate-stream 请求失败（状态 {status}）。",
        stageExecuteRequestFailed: "execute-stage 请求失败（状态 {status}）。",
        agentLogRequestFailed: "agent-logs 请求失败（状态 {status}）。",
        promptUpdateFailed: "更新 Prompt 失败。",
        imageGenerateFailed: "生成图片失败。",
        imageRegenerateFailed: "重生成图片失败。",
        stageFailed: "阶段失败：{stage}"
      },
      action: {
        generateStarted: "已启动生成流程。",
        stageTriggered: "已触发阶段执行：{stage}",
        promptUpdated: "已更新图片 {index} 的 Prompt。",
        imageGenerateTriggered: "已触发图片生成：{index}",
        imageRegenerateTriggered: "已触发图片重生成：{index}"
      },
      stage: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词图谱",
        listing: "Listing 文案",
        visual: "视觉策略",
        adapter: "平台适配",
        export: "导出交付"
      },
      market: {
        medium: "中等竞争",
        high: "高竞争",
        low: "低竞争"
      },
      source: {
        title: "商品理解",
        verified: "数据已确认",
        pending: "等待数据",
        visualDiagnostics: "资产诊断",
        analyzing: "分析中",
        ready: "就绪",
        summary: "摘要",
        expand: "展开详情",
        collapse: "收起详情",
        thumbnailAlt: "缩略图",
        mainImageAlt: "主图",
        emptyImages: "暂无来源图片",
        emptyTitle: "等待来源图片",
        emptyDesc: "连接插件或导入产品后将自动完成商品理解。",
        imageCount: "图片数量：{count}",
        noImages: "暂无图片",
        scanning: "分析中...",
        sectionsTitle: "采集区块",
        sectionsEmpty: "暂无区块数据"
      },
      rawText: {
        title: "原始文本",
        length: "{count} 字符",
        emptyTitle: "暂无原始文本",
        emptyDesc: "运行商品理解后将解析来源文本。"
      },
      productCore: {
        title: "产品核心",
        materials: "材质",
        specs: "规格",
        useCases: "使用场景",
        audience: "适用人群",
        facts: "关键事实",
        emptyTitle: "产品核心待生成",
        emptyDesc: "运行商品理解后将生成核心事实。"
      },
      productSchema: {
        title: "产品数据结构",
        identity: "基础信息",
        titleLabel: "标题",
        brandLabel: "品牌",
        categoryLabel: "类目",
        attributes: "关键属性",
        empty: "暂无属性数据",
        emptyTitle: "属性待生成",
        emptyDesc: "完成结构化阶段后将自动生成关键属性。",
        logistics: "物流信息",
        price: "价格",
        weight: "重量",
        dimensions: "尺寸",
        emptyValue: "—",
        sku: "SKU 概览",
        skuCount: "SKU 数量",
        skuUnit: "条",
        noColors: "暂无颜色",
        sizes: "尺码",
        noSizes: "暂无尺码"
      },
      metrics: {
        images: "图片",
        skus: "SKU",
        attributes: "属性",
        sections: "分段",
        rawText: "原文"
      },
      imageText: {
        title: "图片文字提取",
        count: "图片 {count}",
        empty: "暂无图片文字结果",
        emptyTitle: "暂无识别结果",
        emptyDesc: "运行图片文字提取后将在此显示结果。"
      },
      seo: {
        strength: "搜索强度",
        failed: "SEO 生成失败",
        failedDesc: "暂无 SEO 结果。",
        optimizedKeywords: "优化关键词",
        emptyKeywords: "暂无关键词数据",
        emptyTitle: "SEO 关键词待生成",
        emptyDesc: "完成内容生成后将输出关键词与搜索词。",
        searchVolume: "搜索量区间",
        competition: "意图",
        unknown: "未知",
        clusterLabel: "聚类",
        volumeTier: "热度",
        volumeHigh: "高",
        volumeMedium: "中",
        volumeLow: "低",
        volumeUnknown: "未知",
        searchPipeline: "关键词图谱概览",
        pipelinePending: "关键词图谱生成中...",
        graphTitle: "关键词图谱可视化",
        graphEmpty: "暂无图谱节点。",
        coverageTitle: "覆盖报告",
        missingAspects: "缺失维度",
        duplicates: "重复关键词",
        languageIssues: "语言问题",
        coverageEmpty: "暂无问题"
      },
      keyword: {
        primary: "核心",
        secondary: "次要",
        longTail: "长尾",
        conversion: "转化",
        transactional: "交易",
        information: "信息",
        other: "其他"
      },
      visual: {
        title: "视觉策略",
        subtitle: "图片规划 • {count} 组",
        collapseAll: "全部收起",
        empty: "暂无视觉策略输出，请先运行视觉策略阶段。",
        emptyTitle: "视觉策略待启动",
        emptyDesc: "运行视觉策略阶段后，系统会在这里输出可编辑的提示词与生成结果。",
        emptyStep1: "步骤 1：运行视觉策略",
        emptyStep2: "步骤 2：审核提示词",
        emptyStep3: "步骤 3：生成图片",
        constraintsTitle: "约束条件",
        constraintsEmpty: "暂无约束",
        keywordsTitle: "关键词聚焦",
        keywordsEmpty: "暂无关键词",
        sceneLabel: "场景",
        placeholderMainTitle: "主图方案",
        placeholderMainDesc: "核心展示与主视觉",
        placeholderSceneTitle: "场景方案",
        placeholderSceneDesc: "使用场景与氛围",
        placeholderDetailTitle: "细节方案",
        placeholderDetailDesc: "材质与卖点特写",
        trackPrimary: "序列 01：核心图",
        trackSecondary: "序列 02：细节图",
        collapse: "收起",
        viewMore: "查看 {count} 条",
        statusWaiting: "状态：等待中",
        statusGenerated: "状态：已生成 {current}/{total}",
        statusPrompted: "状态：已规划 {count}",
        generated: "已生成",
        savePrompt: "保存 Prompt",
        generateImage: "生成图片",
        regenerate: "重新生成",
        planBadge: "{index} {type}",
        planDefaultType: "主图",
        planTypeTitle: "{type} 方案",
        planTitle: "图片方案 {index}",
        planAspect: "比例 {ratio}",
        planSubtitle: "图片生成方案"
      },
      listing: {
        title: "Listing 文案",
        writing: "生成中...",
        keywordInput: "SEO 关键词输入",
        keywordEmpty: "暂无关键词提示。",
        emptyTitle: "Listing 待生成",
        emptyDesc: "运行 Listing 阶段后将输出文案结果。",
        fieldTitle: "标题",
        shortDesc: "短描述",
        longDesc: "长描述",
        bullets: "要点",
        emptyBullets: "暂无要点。",
        faq: "FAQ",
        emptyFaq: "暂无 FAQ。"
      },
      adapter: {
        title: "平台适配",
        platformUnknown: "平台",
        summary: "适配概览",
        platform: "平台",
        format: "格式",
        fieldCount: "字段数",
        category: "类目",
        typeId: "类型 ID",
        price: "价格",
        images: "图片",
        attributesCount: "属性数",
        richContent: "富文本",
        missingFields: "缺失字段",
        missingFieldsEmpty: "暂无缺失字段",
        payload: "适配输出",
        emptyPayload: "暂无适配输出。",
        fieldMap: "字段映射",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "请先运行平台适配阶段。"
      },
      export: {
        title: "导出交付",
        exporting: "导出中...",
        platform: "平台",
        category: "类目",
        columns: "字段数",
        rows: "行数",
        templateKey: "模板标识",
        format: "格式",
        version: "模板版本",
        validation: "校验",
        validationPass: "通过",
        validationPending: "待校验 / 有问题",
        fileName: "文件名",
        delivery: "交付",
        ready: "下载就绪。",
        pending: "等待导出数据。",
        download: "下载",
        waiting: "暂无下载链接。",
        validationErrors: "校验问题"
      },
      gallery: {
        main: "主图",
        empty: "暂无生成图片",
        slot: "空位 {index}",
        diffusing: "生成中...",
        mainPlaceholder: "主图位置",
        emptySlot: "图片位 {index}",
        emptyHint: "等待生成",
        waiting: "生成中..."
      },
      marketplace: {
        emptyRich: "暂无富文本素材",
        widgets: "模块 {count}",
        images: "图片 {count}",
        theme: "主题 {theme}",
        emptyMapping: "暂无属性映射"
      },
      richContent: {
        title: "富文本结构",
        summary: "{count} 个模块",
        empty: "暂无富文本模块",
        emptyTitle: "富文本待生成",
        emptyDesc: "富文本阶段完成后，模块与图片将在此展示。",
        emptyPreview: "暂无富文本预览",
        images: "富文本图片",
        emptyImages: "暂无富文本图片",
        version: "版本",
        generatedAt: "生成时间",
        blocks: "{count} 个区块",
        rows: "{count} 行",
        columns: "{count} 列",
        untitled: "未命名模块",
        noDescription: "暂无文字内容",
        moreWidgets: "更多模块：{count}",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "完成属性填充后会自动生成映射。",
        detailHint: "结构已就绪",
        viewDetail: "查看详情",
        collapse: "收起",
        detailTitle: "模块详情",
        detailDesc: "来自富文本代理的结构化输出。",
        jsonTitle: "模块 JSON",
        blocksTitle: "区块预览",
        emptyBlocks: "暂无区块内容",
        galleryTitle: "富文本图片瀑布流",
        emptyGallery: "暂无富文本图片",
        emptyGalleryDesc: "生成富文本后将在此展示图片流。",
        widget: {
          showcase: "图文展示",
          text: "文本模块",
          table: "表格模块",
          video: "视频模块",
          list: "列表模块",
          unknown: "模块"
        }
      },
      video: {
        empty: "视频生成尚未启用。",
        hint: "开启视频阶段后将生成动效素材。",
        recording: "录制中..."
      },
      status: {
        complete: "完成",
        thinking: "思考中",
        processing: "处理中",
        error: "错误",
        waiting: "等待中"
      },
      agent: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词代理",
        listing: "Listing 文案",
        visual: "视觉策略",
        adapter: "平台适配",
        export: "导出交付",
        error: "阶段失败，请查看日志。"
      },
      agentLogs: {
        title: "Agent 运行日志",
        subtitle: "代理实时控制台",
        chainTitle: "AGENT 日志流",
        streaming: "实时更新",
        emptyTitle: "暂无日志",
        emptyDesc: "执行阶段后会在此显示代理日志。",
        logCount: "{count} 条日志",
        lastUpdated: "更新于 {time}",
        duration: "耗时 {duration}",
        expandLog: "展开",
        collapseLog: "收起",
        status: {
          running: "运行中",
          completed: "已完成",
          failed: "失败",
          idle: "空闲"
        },
        event: {
          start: "启动",
          thinking: "思考",
          result: "完成",
          error: "错误",
          cacheHit: "缓存",
          skip: "跳过"
        }
      }
    },

    pricingEngine: {
      title: "一键大卖动态定价引擎",
      subtitle: "多平台利润计算器，内置各平台费率与费用逻辑。",
      calcMode: "计算模式",
      manualEntry: "手动输入",
      myListings: "我的列表",
      destPlatform: "目标平台",
      globalChannels: "{count} 个主流平台可用",
      ready: "就绪",
      soon: "即将推出",
      listingsOzonOnly: "当前仅支持 Ozon 列表同步。",
      inputMode: "输入模式",
      compactMode: "简化",
      fullMode: "完整",
      singlePlatformHint: "大多数卖家专注单一平台。",
      rateSource: "实时汇率来源",
      rateLoading: "更新中...",
      rateUpdated: "更新于 {date}",
      rateError: "汇率异常",
      rateErrorDesc: "获取最新汇率失败，请稍后重试。",
      autoRateOn: "自动汇率",
      autoRateOff: "手动输入",
      refreshRate: "刷新汇率",
      autoRateHint: "已启用自动汇率（Frankfurter API）。",
      manualRateHint: "可手动覆盖汇率。",
      tiktokRegion: "销售地区",
      regionUs: "美国",
      regionUk: "英国",
      regionSea: "东南亚",
      tiktokRegionHint: "切换地区会自动更新佣金/交易费率，可按实际合同调整。",
      selectProduct: "从工作区选择产品",
      viewDetails: "查看详情",
      dataSynced: "数据已同步",
      pricingStrategy: "定价策略",
      targetMargin: "目标利润率 (%)",
      targetProfit: "目标利润 (¥)",
      targetValue: "目标值",
      breakEven: "盈亏平衡",
      suggestedPrice: "建议上架价格",
      rate: "汇率：1 CNY = {rate} {currency}（输入）",
      breakEvenPrice: "盈亏平衡价格：{price}",
      optimizedMsg: "优化为 {value}% 利润率",
      optimizedProfit: "优化为 {value} 利润",
      netProfit: "净利润",
      profitMargin: "利润率",
      targetMet: "目标达成",
      roi: "投资回报率",
      highMargin: "高利润",
      updateListing: "更新列表价格",
      syncDesc: "立即将此价格同步到您工作区的 Ozon 列表。",
      syncSuccess: "价格同步完成",
      syncSuccessDesc: "已将 {price} 同步至 {platform} 列表。",
      feeBreakdown: "平台费用明细",
      catLogic: "类目逻辑已应用",
      commission: "市场佣金",
      lastMile: "最后一公里配送",
      handling: "处理与仓储",
      totalOpCost: "总运营成本",
      costBreakdown: "成本拆解",
      exportReport: "导出报告",
      shareImage: "生成分享图",
      shareReady: "分享图已生成",
      shareReadyDesc: "{platform} 分享图已保存到本地。",
      shareError: "分享图生成失败",
      shareErrorDesc: "无法渲染分享图，请稍后重试。",
      saveScenario: "保存方案",
      prodMetadata: "产品元数据（锁定）",
      prodCategory: "产品类目",
      purchaseCost: "采购成本 (CNY)",
      weight: "重量 (kg)",
      dimensions: "尺寸 (长*宽*高 cm)",
      logistics: "物流",
      air: "空运",
      sea: "海运",
      saveWorkspace: "将此计算保存到您的工作区",
      saveWorkspaceDesc: "创建免费账户，直接同步到 Ozon 并跟踪您的利润历史。",
      createAccount: "创建免费账户",
      basicCosts: "基础成本",
      costCurrency: "成本币种：CNY → {currency}",
      purchaseCostLabel: "采购成本",
      shippingCostLabel: "头程运费",
      packagingCostLabel: "包装/处理成本",
      otherCostLabel: "其他成本",
      shippingCharged: "向买家收取的运费",
      exchangeRateLabel: "汇率 (1 CNY → {currency})",
      platformFees: "平台费用输入",
      categoryLabel: "类目/细分",
      referralFeeLabel: "平台佣金",
      referralRateLabel: "平台佣金/费率",
      referralMinFeeLabel: "最低佣金",
      sellingPlan: "销售计划",
      sellingPlanIndividual: "个人",
      sellingPlanProfessional: "专业",
      sellingPlanFee: "按件收费",
      closingFeeLabel: "成交费",
      fulfillmentMode: "履约方式",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "履约费用",
      storageFee: "仓储费",
      inboundShippingFee: "入仓运费",
      fbmHandlingFee: "自发货处理费",
      platformDiscount: "平台补贴/折扣",
      tax: "税费/VAT",
      sellerDiscount: "卖家优惠",
      affiliateRate: "达人分佣费率",
      affiliatePartnerRate: "联盟分佣费率",
      adsRate: "广告投放费率",
      shippingType: "配送模式",
      shippingPlatform: "平台配送",
      shippingSeller: "卖家配送",
      shippingFbt: "FBT 平台履约",
      fbtFulfillmentFee: "FBT 履约费",
      fbtStorageFee: "FBT 仓储费",
      transactionFeeRate: "交易手续费率",
      transactionFeeFixed: "交易固定费",
      serviceFeeRate: "平台服务费率",
      serviceFeeFixed: "平台固定服务费",
      orderServiceFee: "订单服务费",
      platformSupportFee: "平台支持费",
      platformSupportFeeHint: "部分站点按单收取固定支持费（如马来西亚）。",
      shippingSubsidy: "运费补贴/项目成本",
      ozonPlacementFee: "Ozon 上架/服务费",
      ebayFinalValueRate: "成交费率",
      ebayPerOrderFee: "每单固定费用",
      ebayInsertionFee: "刊登费",
      ebayInternationalRate: "跨境交易费率",
      etsyListingFee: "上架费",
      etsyTransactionFee: "成交费率",
      etsyPaymentRate: "支付处理费率",
      etsyPaymentFixed: "支付固定费",
      etsyOffsiteAdsRate: "站外广告费率",
      totalCost: "总成本",
      platformFeeTotal: "平台费用",
      baseCostTotal: "基础成本",
      platformInsights: "平台提示",
      marketingCosts: "营销与达人费用",
      temuProgram: "Temu 补贴/激励",
      aliexpressProgram: "AliExpress 活动费用",
      shopeeProgram: "Shopee 项目费用",
      temuHeroTitle: "Temu 低佣金模型",
      temuHeroDesc: "多数品类佣金为 0%，可单独跟踪服务费、履约费与补贴。",
      temuHeroTag1: "0% 佣金",
      temuHeroTag2: "服务费可调",
      aliexpressHeroTitle: "AliExpress 类目佣金",
      aliexpressHeroDesc: "佣金随类目变化，建议同步建模支付/服务费。",
      aliexpressHeroTag1: "5-8% 佣金",
      aliexpressHeroTag2: "无年费",
      shopeeHeroTitle: "Shopee 费用叠加",
      shopeeHeroDesc: "叠加佣金、交易费与项目费，快速估算净利。",
      shopeeHeroTag1: "多费率",
      shopeeHeroTag2: "项目可选",
      shopeeHeroTag3: "支持费",
      amazonHighlightTitle: "Amazon 费用快照",
      amazonHighlightDesc: "同时考虑佣金、销售计划与 FBA/FBM 成本，快速确定价格下限。",
      ebayHighlightTitle: "eBay 成交费提示",
      ebayHighlightDesc: "成交费通常基于商品+运费+税费，叠加每单固定费与跨境费。",
      walmartHighlightTitle: "Walmart 佣金提醒",
      walmartHighlightDesc: "佣金按类目变化，WFS 等履约成本可在此基础上补充。",
      etsyHighlightTitle: "Etsy 列表周期提示",
      etsyHighlightDesc: "上架费、成交费、支付费与站外广告可按单配置。",
      platformNotesAmazon: "包含类目佣金、按件收费（个人计划）以及 FBA/FBM 履约费用。",
      platformNotesTikTok: "包含类目佣金、交易手续费、达人分佣与可选 FBT 履约费用。",
      platformNotesOzon: "佣金与类目相关，末端配送与处理费用按重量估算。",
      platformNotesEbay: "成交费基于商品+运费+税费，另加每单固定费用。",
      platformNotesWalmart: "佣金随类目变化，履约成本未在此快速计算中展开。",
      platformNotesEtsy: "包含上架费、成交费、支付处理费及可选站外广告费。",
      platformNotesTemu: "Temu 佣金可能为 0%，但服务费与履约成本需按项目填入。",
      platformNotesAliExpress: "AliExpress 佣金按类目变化，部分地区有支付/服务费。",
      platformNotesShopee: "Shopee 通常包含佣金、交易费与可选项目/支持费。",
      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    copyright: {
      nav: "版权资产",
      title: "版权与法律资产",
      subtitle: "一站式证据包，用于即时知识产权执法和版权保护。",
      statusActive: "法律安全状态：活跃",
      protected: "受保护列表",
      certified: "认证包",
      onChain: "链上证明",
      ready: "执法就绪",
      filter: "筛选",
      searchPlaceholder: "按产品名称或 SKU 搜索...",
      table: {
        preview: "资产预览",
        product: "产品 / 平台",
        status: "认证状态",
        bundle: "法律证据包",
        actions: "操作",
        download: "下载完整法律包",
        previewBtn: "预览",
        synced: "同步：",
        certified: "已认证",
        docs: "高清文档"
      },
      bundleNav: {
        back: "返回",
        details: "包详情",
        downloadCurrent: "下载当前",
        downloadAll: "下载完整证据包 (.zip)",
        contents: "包内容",
        letter: "官方授权书",
        psd: "取证 PSD 源文件",
        watermark: "神经水印分析",
        blockchain: "区块链创作证明",
        dispute: "知识产权争议模板",
        securityHash: "安全哈希"
      },
      tabs: {
        docView: "文档视图",
        metaDash: "元数据仪表板",
        xray: "X 射线分析"
      },
      letter: {
        title: "平台授权书",
        seal: "已加盖官方印章",
        certTitle: "真实性证书",
        certSub: "官方法律证据记录",
        docId: "文档 ID",
        date: "签发日期",
        subject: "主题识别",
        prodName: "产品名称",
        sku: "资产 SKU",
        jurisdiction: "平台管辖权",
        grade: "证据等级",
        gradeValue: "取证认证",
        body1: "本文件构成正式法律证据，证明上述资产已在一键大卖知识产权保护库中验证和注册。",
        body2: "验证包括 PSD 源文件的取证分析、在转换过程中持久存在的神经水印嵌入，以及公共区块链账本上的时间戳。",
        officer: "法律合规官",
        signature: "数字签名：",
        footer1: "安全取证链：#991-AUTH-Z",
        footer2: "专有证据格式 v4.2"
      },
      psd: {
        reportTitle: "取证元数据报告",
        verified: "元数据已验证",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 个图层",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "技术属性",
        colValue: "取证值",
        created: "创建时间戳",
        createdDesc: "文件系统起源日期",
        fingerprint: "神经指纹 (SHA-256)",
        fingerprintDesc: "唯一数学标识",
        author: "作者 ID",
        authorDesc: "已验证的创作者签名",
        software: "软件签名",
        softwareDesc: "引擎和构建版本",
        seed: "嵌入水印种子",
        seedDesc: "持久神经追踪器",
        engine: "分析引擎",
        integrity: "完整性检查",
        secure: "100% 安全"
      },
      watermark: {
        title: "神经水印 X 射线",
        scanning: "正在扫描神经层...",
        status: "状态",
        detected: "检测到神经水印",
        score: "弹性评分",
        stable: "加密稳定",
        encodingTitle: "神经编码",
        encodingDesc: "此水印在生成扩散过程中嵌入神经层。与传统像素叠加不同，此签名是图像权重的一部分，使其能够抵抗基于 AI 的擦除、裁剪或噪声注入。",
        latentSeed: "潜在种子",
        engineSig: "引擎签名",
        inference: "推理时间",
        confidence: "扫描置信度"
      },
      blockchain: {
        title: "区块链验证证书",
        subtitle: "资产来源的不可变证明",
        verified: "已链上验证",
        network: "网络：",
        networkVal: "Arbitrum One / 以太坊主网",
        txHash: "交易哈希 (TxID)",
        signature: "数字签名 (EdDSA)",
        block: "区块高度",
        timestamp: "时间戳 (UTC)",
        legalNoteTitle: "法律可采性说明",
        legalNoteDesc: "此证书提供加密安全的时间戳和作者记录。记录永久锚定在 Arbitrum L2 网络上，继承以太坊结算层的完整安全性。",
        scan: "扫描验证",
        verifStatus: "验证状态",
        protocol: "OneClick-ChainLink 预言机 v2",
        confirmed: "已确认",
        confirmations: "节点确认数",
        integrity: "数据完整性",
        secure: "100% 安全"
      },
      dispute: {
        preview: "预览文档",
        settings: "模板设置",
        language: "语言：",
        title: "知识产权侵权删除通知",
        subtitle: "自动法律合规文档",
        to: "致：法律合规部门",
        ref: "参考：",
        date: "日期：",
        s1Title: "1. 所有权声明",
        s1Body: "本人，签署人，特此在伪证处罚下声明，本人是本文所述知识产权版权所有者的授权代表。一键大卖公司对与此声明相关的专有数字资产拥有专有权利。",
        s2Title: "2. 侵权描述",
        s2Body: "我们已在以下位置发现未经授权使用我们的版权视觉资产：",
        s2Url: "URL：",
        s2Desc: "此列表使用的专有产品图像和创意布局与我们的原创作品相同或实质相似。",
        s3Title: "3. 原创性证据",
        s3Body: "争议资产的原创性和创建日期通过附件证据包中包含的以下加密方法进行验证：",
        s3Item1: "神经水印：",
        s3Item1Desc: "嵌入图像数据中与我们主文件匹配的隐形取证标记。",
        s3Item2: "区块链证明：",
        s3Item2Desc: "记录在 Arbitrum 主网上的不可变创作证明。",
        s4Title: "4. 法律声明",
        s4Body: "本人真诚相信，以被投诉的方式使用该材料未经版权所有者、其代理人或法律授权。本通知中的信息准确无误。我们要求立即删除侵权材料。",
        signature: "授权签名",
        division: "一键大卖公司知识产权保护部",

        sidebar: {
          verifStatus: "验证状态",
          link: "侵权链接",
          linkDesc: "已提供 URL",
          psd: "取证 PSD",
          psdDesc: "图层数据已验证",
          evidence: "其他证据",
          missing: "缺少 2 个模块",
          prob: "执法概率",
          lowConf: "低置信度",
          guide: "证据完成指南",
          scan: "神经水印扫描",
          boost30: "缺失：+30% 提升",
          complete: "立即完成",
          proof: "区块链证明",
          boost22: "缺失：+22% 提升",
          autoFiling: "自动提交",
          filingNote: "置信度高于 80% 前不建议提交。",
          autoSubmit: "自动提交声明",
          processing: "处理中...",
          submitted: "声明已提交"
        }
      }
    },

    workspace: {
      nav: "工作区",
      heroTitle: "创建您的第一个 AI 列表",
      heroSub: "粘贴链接 → AI 优化 → 一键发布。在几秒钟内转换产品数据。",
      returningLabel: "欢迎回来",
      returningTitle: "继续优化你的最新列表",
      returningSub: "你的最近导入已准备就绪。继续完善内容或进入任务中心查看进度。",
      startBtn: "开始导入产品",
      taskCenterEntry: "进入任务中心",
      pasteLink: "粘贴链接",
      pasteDesc: "从速卖通、1688、亚马逊等平台导入。",
      uploadMedia: "上传媒体",
      uploadDesc: "拖放图片、视频或 ZIP 文件。",
      batchImport: "批量导入",
      batchDesc: "通过 CSV 或多个 URL 批量导入产品。",
      recentImports: "最近导入",
      viewHistory: "查看历史",
      extRequired: "需要 Chrome 扩展",
      extNotFound: "未找到",
      extDesc: "从主要平台安全收集数据所必需。",
      extDownload: "下载扩展",
      watchTutorial: "观看视频教程",
      readGuide: "阅读用户指南",
      status: {
        processing: "处理中",
        ready: "待发布",
        uploaded: "已上传",
        failed: "失败"
      },
      loadFailed: "加载最近导入失败。",
      loadHint: "请重试以获取最新任务进度。",
      retry: "重试",
      emptyTitle: "暂无导入记录",
      emptyDesc: "先导入一个产品链接，就能在这里查看进度。",
      loginTitle: "登录后查看导入记录",
      loginDesc: "登录即可同步你的导入历史与流程进度。",
      loginAction: "立即登录",
      unknownSource: "未知来源"
    },

    // 任务中心
    taskCenter: {
      nav: "任务中心",
      breadcrumb: {
        workspace: "工作区",
        taskCenter: "任务中心"
      },
      title: "任务中心",
      subtitle: "管理单品与批量商品 AI 流程。",
      target: {
        label: "目标：",
        platform: "Amazon",
        region: "美国",
        language: "英语"
      },
      sync: {
        label: "同步进度",
        value: "85%"
      },
      reviewChanges: "查看变更",
      createTask: "创建新任务",
      dropdown: {
        singleTitle: "单品任务",
        singleDesc: "通过单个链接导入",
        batchTitle: "批量导入任务",
        batchDesc: "通过多链接或 CSV 导入"
      },
      table: {
        source: "来源",
        task: "任务名称 / ID",
        status: "状态",
        progress: "AI 进度",
        createdAt: "创建时间",
        action: "操作"
      },
      status: {
        active: "进行中",
        completed: "已完成",
        waiting: "等待中",
        idle: "空闲",
        collect_pending: "等待采集",
        assigned: "已分配采集",
        collecting: "采集中",
        collect_uploading: "上传中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "已取消",
        collect_expired: "已超时",
        pending_generate: "等待生成",
        generating: "生成中",
        ready: "待上传",
        uploaded: "已上传",
        generation_failed: "生成失败"
      },
      batchLabel: "批量",
      itemsLabel: "件",
      progress: {
        processing: "处理中",
        copywriting: "AI 文案",
        inQueue: "排队中"
      },
      stage: {
        collect_pending: "等待采集",
        collect_assigned: "插件已接单",
        collect_preparing: "准备采集",
        collect_scrolling: "页面滚动中",
        collect_collecting: "采集数据中",
        collect_saving: "保存数据中",
        collect_uploading: "上传素材中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "任务已取消",
        collect_expired: "任务已超时",
        generate_pending: "等待生成",
        generate_info_structure: "商品理解中",
        generate_seo: "SEO 关键词图谱生成中",
        generate_listing: "Listing 文案生成中",
        generate_visual: "视觉策略生成中",
        generate_adapter: "平台适配中",
        generate_export: "导出交付中",
        generate_ready: "生成完成待上传",
        generate_uploaded: "已上传",
        generate_failed: "生成失败"
      },
      actions: {
        workspace: "工作区",
        preview: "预览",
        generate: "开始生成",
        retry: "重试",
        retryCollect: "重新采集",
        retryGenerate: "重试生成",
        pending: "待处理"
      },
      tooltips: {
        previewLocked: "采集完成后可预览",
        previewUnavailable: "采集完成后才可预览",
        previewCollecting: "采集中，稍后可预览。",
        previewFailed: "采集失败，暂无可预览内容。",
        expand: "展开批量任务",
        collapse: "收起批量任务",
        errorFallback: "任务执行失败，请重试或联系客服。",
        workspaceLocked: "采集与 AI 处理完成后可进入工作区"
      },
      errorSummaryDefault: "任务执行异常，请重试。",
      pagination: {
        showing: "显示 4 / 42 个任务"
      },
      banner: "多代理流水线已启用并启用品牌保护。目标：Amazon 美国。任务完成后可进入工作区。",
      loading: "任务加载中...",
      emptyTitle: "这里暂时还没有任务",
      emptyHint: "导入单个商品或创建批量任务后，将在这里查看进度。",
      emptyAction: "创建新任务",
      loginHint: "登录后即可查看任务中心",
      loginDesc: "登录账户以追踪采集、生成与上传的完整进度。",
      loginAction: "去登录",
      loadFailed: "任务加载失败",
      retryHint: "请刷新页面或稍后重试。",
      retryAction: "重新加载",
      modal: {
        title: "批量导入任务",
        subtitle: "一次性向目录中添加多个商品。",
        supportedPlatforms: "支持的平台",
        productUrls: "商品链接",
        totalLinks: "唯一链接总数：",
        onePerLine: "每行一个链接",
        duplicateWarning: "检测到 3 个重复链接，已自动移除。",
        uploadTitle: "上传 CSV 或 Excel 文件",
        uploadSubtitle: "单次最多 500 条，确保 A 列为链接。",
        uploadHint: "需要模板？下载我们的 CSV/Excel 示例",
        browseFiles: "浏览文件",
        cancel: "取消",
        startBatch: "开始批量导入"
      }
    },

    import: {
      nav: "导入产品",
      title: "导入产品来源",
      subtitle: "连接您的供应商链接或上传资产以开始 AI 优化。",
      extActive: "浏览器扩展活跃",
      connected: "已连接",
      extActiveDesc: "伴侣扩展活跃。您可以直接从供应商导入。",
      extSettings: "扩展设置",
      extMissing: "需要扩展",
      extMissingDesc: "请安装伴侣扩展以在本地抓取产品数据。",
      downloadBtn: "下载扩展",
      downloadGuide: "正在打开扩展下载页...",
      urlLabel: "产品 URL",
      urlSub: "支持速卖通、1688、亚马逊、Shopify 等。",
      urlPlaceholder: "在此粘贴您的产品链接...",
      urlRequired: "请先填写产品链接。",
      authRequired: "请先登录后再开始采集。",
      guestFailed: "无法创建游客体验，请稍后重试。",
      importBtn: "导入",
      submitting: "正在提交...",
      collectFailed: "提交采集任务失败。",
      uploadTitle: "上传媒体资产",
      uploadSub: "将图片、视频或 ZIP 文件夹拖放到此处",
      chooseFiles: "选择文件",
      alternative: "替代方式",
      targetGlobal: "目标：全球市场",
      taskCenterEntry: "任务中心",
      taskCenterHint: "登录后可在任务中心查看进度。",
      recent: {
        title: "最近任务",
        subtitle: "最新导入任务正在处理中",
        taskId: "任务ID",
        source: "来源链接",
        createdAt: "创建时间",
        statusHint: "采集完成后会在任务中心通知。",
        viewTaskCenter: "查看任务中心",
        loginToView: "登录查看",
        status: {
          processing: "处理中",
          queued: "排队中",
          completed: "已完成",
          failed: "失败"
        }
      },
      success: {
        title: "任务已创建",
        subtitle: "我们已开始采集商品数据。您可以继续预览或前往任务中心跟踪进度。",
        taskId: "任务ID",
        source: "来源链接",
        status: "状态",
        statusProcessing: "处理中",
        statusHint: "采集正在后台执行。",
        continue: "继续导入",
        goTaskCenter: "前往任务中心",
        loginTaskCenter: "登录查看任务",
        loginHint: "登录后可在任务中心查看进度。"
      }
    },

    // 基本信息设置
    generalInfo: {
      title: "基本信息",
      subtitle: "管理您的账户详情和个人资料。",
      proPlan: "专业版",
      upgradePlan: "升级套餐",
      uploadNew: "上传新头像",
      remove: "移除",
      photoHint: "建议尺寸不小于 400x400 像素，支持 JPG 或 PNG 格式。",
      fullName: "姓名",
      fullNamePlaceholder: "例如：张三",
      professionalEmail: "工作邮箱",
      emailPlaceholder: "name@company.com",
      companyName: "公司名称",
      companyPlaceholder: "请输入公司名称",
      cancel: "取消",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      footer: "平台设置"
    },

    // 账单与积分
    billing: {
      title: "账单与积分",
      subtitle: "查看积分余额、图片扣费规则与完整账单记录。",
      currentBalance: "当前余额",
      credits: "积分",
      balanceHint: "订阅积分每月清零，充值积分永久有效。",
      topUp: "充值",
      subscriptionPlan: "订阅套餐",
      proPlanPrice: "专业版 · 每月560积分（$49）",
      nextRenewal: "下次续费日期：2026年2月24日",
      managePlan: "管理套餐",
      billingHistory: "账单记录",
      date: "日期",
      description: "描述",
      amount: "金额",
      invoice: "发票",
      proSubscription: "专业版订阅（560 积分）",
      creditTopUp: "积分充值 (300 积分)",
      downloadAll: "下载全部发票",
      footer: "所有价格以美元计价，订阅积分每月清零，充值积分永久有效。",
      footerCopyright: "财务运营中心",
      pricingTitle: "计费规则",
      ruleImagePrice: "图片生成 2 积分/张（$0.2）。",
      ruleExchange: "1 积分 = $0.1，单张图片成本约 $0.15。",
      ruleRollover: "订阅积分每月清零，充值积分永久有效。",
      ruleGift: "注册赠送 10 积分，游客赠送 2 积分。",
      faqTitle: "常见问题",
      faq: {
        q1: "为什么只对图片收费？",
        a1: "图片生成是主要成本项，其它模块免费以保持定价透明。",
        q2: "订阅积分会结转吗？",
        a2: "不会，订阅积分每月清零；充值积分永久有效。",
        q3: "生成失败会退积分吗？",
        a3: "会，系统会根据失败记录自动退还积分。",
        q4: "订阅期间还能充值吗？",
        a4: "可以，充值积分叠加到订阅积分之上。",
      }
    },

    purchaseCredits: {
      title: "充值积分",
      subtitle: "选择充值包用于图片生成（2 积分/张）。",
      priceHint: "1 积分 = $0.1，充值积分永久有效。",
      creditsLabel: "积分",
      imagesLabel: "可生成",
      imagesUnit: "张",
      bestValue: "最划算",
      saveLabel: "立省",
      featureNeverExpire: "充值积分永久有效",
      featureBatch: "支持批量生成",
      featureNoBatch: "不支持批量生成",
      featurePriority: "优先支持",
      paymentTitle: "支付方式",
      purchaseButton: "完成支付",
      processing: "正在处理支付...",
      success: "支付成功，积分已到账。",
      acceptedMethods: "支持方式",
      visaLabel: "Visa 尾号 4242",
      visaExpire: "到期时间 12/26",
      wechat: "微信支付",
      wechatDesc: "快捷安全的移动支付",
      alipay: "支付宝",
      alipayDesc: "全球支付解决方案",
      addNew: "添加新的支付方式",
      addNewDesc: "信用卡、微信支付或支付宝",
      security: "Stripe SSL 加密保障",
      total: "支付总额",
      footer: "© 2026 一键大卖财务运营中心"
    },

    subscription: {
      title: "管理订阅套餐",
      subtitle: "选择符合图片生成需求的套餐。",
      currentPlanLabel: "当前套餐",
      currentPlanBadge: "当前套餐",
      activeTag: "生效中",
      nextBilling: "下次扣费日期：",
      nextBillingDate: "2026年2月24日",
      activeCycle: "当前周期",
      monthly: "月付",
      yearly: "年付",
      yearlySave: "送 2 个月",
      billedMonthly: "按月计费",
      billedYearly: "按年计费（10个月价格）",
      rolloverHint: "订阅积分每月清零，充值积分永久有效。",
      perMonth: "/月",
      perYear: "/年",
      planStarterDesc: "适合个人或刚起步团队。",
      planProDesc: "成长团队首选。",
      planBusinessDesc: "适合高频卖家与工作室。",
      featureStarter1: "基础批量生成",
      featureStarter2: "高清素材下载",
      featureStarter3: "标准支持（48 小时）",
      featurePro1: "高级批量生成",
      featurePro2: "高清 + 4K 素材下载",
      featurePro3: "优先支持（12 小时）",
      featurePro4: "专属增长经理",
      featureBusiness1: "高并发批量生成",
      featureBusiness2: "品牌素材包导出",
      featureBusiness3: "优先支持 + 成功经理",
      featureBusiness4: "流程自动化指导",
      downgrade: "降级套餐",
      upgrade: "升级套餐",
      activePlan: "当前套餐",
      contactSales: "联系销售",
      customTitle: "需要定制方案？",
      customDesc: "我们可以根据你的产出与流程定制方案。",
      consultSales: "咨询销售",
      cancel: "取消订阅",
      cancelConfirm: "确定要取消订阅吗？",
      footer: "© 2026 一键大卖财务运营中心"
    },

    // 订阅方案设置
    subscriptionSettings: {
      title: "管理订阅方案",
      subtitle: "选择最适合你业务的套餐。",
      currentPlanLabel: "当前套餐：",
      currentPlan: "专业版",
      active: "已启用",
      nextBillingLabel: "下次扣费日期：",
      nextBillingDate: "2024 年 11 月 24 日",
      activeCycleLabel: "当前周期：",
      monthly: "月度",
      yearly: "年度",
      save20: "节省 20%",
      billedAnnually: "按年计费，享受最大优惠",
      billed: "按{cycle}计费",
      perMonth: "/月",
      plans: {
        starter: {
          title: "入门版",
          desc: "适合个人与小型创业团队。"
        },
        pro: {
          title: "专业版",
          desc: "最受成长型团队欢迎。"
        },
              },
      features: {
        starterCredits: "每月 500 积分",
        starterAgents: "基础 AI 代理权限",
        starterSupport: "邮件支持（48 小时内）",
        customIntegrations: "自定义集成",
        proCredits: "每月 2,000 积分",
        proAgents: "全部 AI 代理权限",
        proSupport: "优先支持（12 小时内）",
        proIntegrations: "最多 5 个集成",
              },
      buttons: {
        downgrade: "降级",
        activePlan: "当前方案",
        contactSales: "联系销售",
        consultSales: "咨询销售",
        cancelSubscription: "取消订阅"
      },
      currentBadge: "当前方案",
      custom: {
        title: "需要定制方案？",
        desc: "我们的销售专家可为高频业务量身定制解决方案。"
      },
      alerts: {
        downgrade: "正在降级到入门版...",
        contactSales: "正在联系销售团队...",
        openChat: "正在打开销售聊天...",
        cancelConfirm: "确定要取消吗？",
        cancelRequested: "已提交取消订阅请求。"
      }
    },

    // 安全设置
    securitySettings: {
      title: "安全",
      subtitle: "管理账号保护、认证方式与活跃会话，确保数据安全。",
      twoFactor: {
        title: "双重验证 (2FA)",
        desc: "启用后，新的设备登录需要验证码，提升账号安全。"
      },
      password: {
        title: "密码管理",
        updated: "最后更新于 2023 年 10 月 12 日",
        reset: "重置密码"
      },
      sessions: {
        title: "活跃会话",
        subtitle: "以下是已登录设备。如不认识请撤销。",
        current: "当前",
        activeNow: "在线中",
        signOut: "退出登录",
        chromeDevice: "macOS 上的 Chrome",
        chromeLocation: "美国旧金山 • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "英国伦敦 • 2 天前"
      },
      cancel: "取消",
      saving: "保存中...",
      saved: "已保存",
      save: "保存更改",
      signOutAlert: "正在退出该设备...",
      footer: "安全基础设施"
    },

    // 通知设置
    notificationsSettings: {
      title: "通知设置",
      subtitle: "管理系统更新和提醒的接收方式。",
      table: {
        type: "通知类型",
        email: "邮箱",
        push: "推送"
      },
      listing: {
        title: "上架成功",
        desc: "商品发布时提醒"
      },
      credit: {
        title: "积分预警",
        desc: "余额低于 10% 时提醒"
      },
      updates: {
        title: "更新与发布",
        desc: "新功能与平台改进"
      },
      mute: {
        title: "静默全部通知",
        desc: "暂时关闭平台内所有提醒与邮件"
      },
      discard: "放弃更改",
      saving: "保存中...",
      saved: "偏好已保存",
      save: "保存偏好"
    },

    // 集成
    integrations: {
      title: "平台集成",
      subtitle: "连接您常用的工具和电商平台，优化工作流程。",
      connected: "已连接",
      connect: "连接",
      shopify: "Shopify",
      shopifyDesc: "同步商品和库存数据。",
      amazon: "亚马逊卖家",
      amazonDesc: "导入商品列表并管理订单。",
      ozon: "Ozon",
      ozonDesc: "俄罗斯电商平台集成。",
      slack: "Slack",
      slackDesc: "在频道中接收实时通知。"
    },

    // 平台偏好
    platformPreferences: {
      title: "平台偏好设置",
      subtitle: "自定义默认的目标市场和 AI 智能体行为。",
      targetMarketplace: "默认目标市场",
      primaryPlatform: "主要平台",
      targetCountry: "目标国家",
      russia: "俄罗斯",
      us: "美国",
      uk: "英国",
      germany: "德国",
      aiPersona: "AI 写作风格",
      poweredBy: "由 LexiCopy 驱动",
      persuasive: "营销说服型",
      professional: "专业严谨型",
      minimalist: "简约精炼型",
      seoFocused: "SEO 优先型",
      personaHint: "此风格将默认应用于所有新的产品描述和广告文案。",
      brandProtection: "自动品牌保护",
      brandName: "默认品牌名称",
      brandPlaceholder: "输入用于水印的品牌名称...",
      autoWatermark: "自动添加 AI 水印",
      autoWatermarkDesc: "为所有新生成的内容添加品牌标识保护",
      imagePreferences: "图片处理偏好",
      deepEnhancement: "深度图像增强",
      deepEnhancementDesc: "AI 超分辨率和清晰度提升",
      autoBgRemoval: "自动去除背景",
      autoBgRemovalDesc: "生成电商平台规范的干净素材",
      autoSaveHint: "更改将自动保存到您的工作区配置。",
      resetDefaults: "恢复默认设置",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      footer: "全球工作区"
    },

    // 资产管理
    assets: {
      nav: "资产库",
      title: "资产管理",
      subtitle: "管理和同步您的 AI 优化商品列表。",
      newOptimization: "新建优化",
      platformAll: "平台：全部",
      statusPublished: "状态：已发布",
      last30Days: "近 30 天",
      clearFilters: "清除筛选",
      score: "评分",
      published: "已发布",
      draft: "草稿",
      failed: "失败",
      retry: "重试",
      edit: "编辑",
      view: "查看",
      addNewListing: "添加新商品",
      addNewDesc: "上传原始图片进行 AI 增强。",
      assetsSelected: "已选资产",
      batchDownload: "批量下载",
      batchSync: "批量同步",
      delete: "删除",
      showingAssets: "显示第 1 至 {count} 项，共 {total} 项资产",
      searchPlaceholder: "搜索资产..."
    },

    // 导入预览
    importPreview: {
      nav: "商品预览",
      breadcrumb: "工作区",
      collectStatus: "采集状态",
      collectQueued: "排队中",
      collecting: "采集中",
      collectCompleted: "已完成",
      collectFailed: "采集失败",
      collectingHint: "插件正在后台采集数据。",
      collectProgressTitle: "采集进度",
      collectProgressHint: "正在与插件同步，请在此查看实时进度。",
      progressLabel: "进度",
      stepQueued: "等待插件接收",
      stepAssigned: "插件已接收",
      stepCollecting: "采集中",
      stepUploading: "上传中",
      stepCompleted: "采集完成",
      currentStage: "当前阶段",
      stage: {
        pending: "等待插件接收",
        assigned: "任务已分配",
        preparing: "准备页面",
        scrolling: "滚动页面",
        collecting: "采集中",
        saving: "保存数据",
        uploading: "上传资源",
        cached: "加载缓存结果",
        completed: "已完成"
      },
      authRequired: "请先登录后查看采集进度。",
      pollFailed: "获取采集进度失败。",
      previewFailed: "加载采集数据失败。",
      attributeList: "其他属性",
      detailHighlights: "详情要点",
      noHighlights: "暂无可用的详情要点。",
      skuStructure: "SKU 结构",
      skuStock: "库存",
      skuVariant: "规格",
      noSkuData: "暂无 SKU 结构数据。",
      productDetails: "商品详情",
      detailSummary: "详情摘要",
      noDetailSummary: "暂无详情摘要。",
      noImages: "暂无图片",
      dataOverview: "采集概览",
      overviewImages: "图片数量",
      overviewSkus: "SKU 数量",
      overviewAttributes: "属性数量",
      overviewSections: "区块数量",
      overviewRawText: "原始文本长度",
      characters: "字符",
      noOverview: "暂无采集概览数据。",
      flags: "采集标记",
      noFlags: "暂无采集标记。",
      flagEnabled: "是",
      flagDisabled: "否",
      dimensionsInfo: "尺寸与重量",
      noDimensions: "暂无尺寸信息。",
      imageTags: "图片标签",
      noImageTags: "暂无图片标签信息。",
      rawText: "原始文本",
      noRawText: "暂无原始文本。",
      rawSections: "原始区块",
      validationWarning: "数据校验问题",
      issuesDetected: "个问题可能影响 AI 优化效果",
      manualReview: "需要人工审核",
      allResolved: "所有问题已解决",
      source: "来源",
      marketPricing: "市场定价分析",
      supplierPrice: "供应商价格 (1688)",
      priceRange: "价格区间",
      invalid: "无效",
      aiSuggest: "AI 建议",
      marketAvg: "市场均价",
      suggestedAiPrice: "AI 建议价格",
      profitMargin: "利润率",
      marginDesc: "扣除常规运费后预估净利润率 72.4%。",
      aiStrategy: "AI 优化策略",
      seoEnhancements: "SEO 增强",
      seoDesc: "将添加「矫正支撑」和「透气网面」等关键词，预计提升搜索曝光 35%。",
      imageRefinement: "图片优化",
      imageDesc: "AI 将去除 1688 水印，并为主图生成场景化背景。",
      marketPositioning: "市场定位",
      positionDesc: "定位「轻奢品质」细分市场，主打城市耐用性。",
      skuAttributes: "SKU 属性",
      colorVariants: "颜色款式",
      sizeAvailability: "尺码可选",
      currentStock: "当前库存",
      shippingEst: "预计发货",
      shippingLocal: "3-5 天 (本地)",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      aiAutofill: "AI 自动补全缺失数据",
      dataFilled: "AI 已补全数据",
      viewMetadata: "查看完整原始数据",
      lowRes: "低分辨率",
      dataRequired: "需要数据校验",
      readyOptimize: "已就绪，可优化",
      agentsPaused: "优化智能体已暂停",
      aiFillAll: "AI 补全所有缺失数据",
      saveDraft: "保存草稿",
      saved: "已保存",
      startOptimization: "开始 AI 优化",
      optimized: "已优化",
      fixIssues: "修复问题以提升 AI 效果",
      exportToStore: "导出到店铺",
      exporting: "导出中...",
      exported: "已导出",
      exportSuccess: "商品已成功导出到店铺！"
    },

    review: {
      nav: "AI 数据审核",
      validating: "AI 正在根据产品类目建议值。",
      validated: "AI 优化成功。所有字段已验证。",
      pending: "需要人工审核",
      completed: "审核完成",
      marketPricing: "市场定价比较",
      supplierPrice: "供应商价格",
      invalid: "无效",
      marketAvg: "市场均价",
      suggestedPrice: "AI 建议价格",
      aiGenerated: "AI 生成",
      marginAnalysis: "利润率分析",
      marginDesc: "本地化运费后估计净利润率为 72.4%。",
      strategy: "AI 优化策略",
      seoEnhance: "SEO 增强",
      seoDesc: "将添加优化关键词以提高可见性。",
      imgRefine: "图像优化",
      imgDesc: "AI 将去除水印并生成生活场景背景。",
      marketPos: "市场定位",
      marketPosDesc: "针对耐用性定位于'经济高端'细分市场。",
      skuAttrs: "SKU 属性",
      originalData: "原始数据",
      colorVariants: "颜色变体",
      sizeAvail: "尺寸可用性",
      stock: "当前库存",
      shipping: "运费估算",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      confirmBtn: "确认所有 AI 建议",
      autofillBtn: "AI 自动填充缺失数据",
      saveDraft: "保存草稿",
      applyAi: "应用 AI 更改",
      startAi: "开始 AI 优化",
      exportBtn: "导出到店铺"
    }
  }
};
